import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Zap, TrendingUp, AlertCircle, Calculator, Plus, Trash2, Save, Wrench, Home } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseGeneric } from "@/types/supabase-custom";
import { testSupabaseConnection, withRetry, checkSupabaseConfig } from "@/utils/supabaseConnection";
import { resetRevisaoOnChanges } from "@/utils/resetRevisaoOnChanges";
import { useAuth } from "@/hooks/useAuthHook";
import { modulosService } from "@/services/modulosService";
import { inversoresService } from "@/services/inversoresService";
import { bateriasService } from "@/services/bateriasService";
import type { ModuloFotovoltaico } from "@/types/modulos";
import type { Inversor } from "@/types/inversores";
import type { Bateria } from "@/types/baterias";
import { isProdutoLeadSolarHibrido } from "@/constants/leadsProduto";
import { formatBrazilianCurrencyValue, formatCurrencyInput, parseBrazilianCurrency } from "@/utils/formatters";

// Tipagem temporária para contornar problemas do Supabase
// Usando supabase diretamente

// Tipos para insumos
interface Insumo {
  id: string;
  tipo: string;
  nome: string;
  quantidade: number;
  preco: number;
  total: number;
  Insumos?: InsumoData[];
  Data?: InsumoData[] | InsumoData;
}

// Tipo para resposta da API de insumos
interface InsumosResponse {
  Insumos?: InsumoData[];
  Data?: InsumoData[] | InsumoData;
}

interface InsumoData {
  id: string;
  tipo: string;
  nome: string;
  quantidade: number;
  preco: number;
  total: number;
  Tipo?: string;
  Quantidade?: number;
  Potencia?: number;
  potencia?: number;
  Descricao?: string;
  descricao?: string;
  Nome?: string;
  PotenciaKW?: number;
  potenciaKW?: number;
  PotenciaW?: number;
  potenciaW?: number;
  Codigo?: string;
  codigo?: string;
  Valor?: number;
  valor?: number;
  LinkFolheto?: string;
  link_folheto?: string;
  Imagem?: string;
  imagem?: string;
  descricao_produto?: string;
  Descricao_Produto?: string;
  [key: string]: unknown;
}

// Filter data from filtros.json
const filtersData = {
  "Dados": [
    {
      "Descricao": "POTÊNCIA DO GERADOR (KWP)",
      "Id": 3,
      "Valor_Max": 30000,
      "Valor_Min": 0
    },
    {
      "Descricao": "TENSÃO SAIDA DO INVERSOR",
      "Id": 5,
      "Valores": [
        {
          "Descricao": "120V",
          "Id": 138
        },
        {
          "Descricao": "220V",
          "Id": 126
        },
        {
          "Descricao": "380V",
          "Id": 118
        }
      ]
    },
    {
      "Descricao": "INVERSOR SOLAR",
      "Id": 6,
      "Valores": [
        {
          "Descricao": "DEYE",
          "Id": 540
        },
        {
          "Descricao": "GROWATT",
          "Id": 245
        },
        {
          "Descricao": "NEP",
          "Id": 594
        }
      ]
    },
    {
      "Descricao": "PAINEL SOLAR",
      "Id": 8,
      "Valores": [
        {
          "Descricao": "DAH 585W BIF N-TYPE",
          "Id": 622
        },
        {
          "Descricao": "DAH 610W BIF N-TYPE",
          "Id": 623
        },
        {
          "Descricao": "JA MONO 550W",
          "Id": 553
        },
        {
          "Descricao": "JINKO MONO 470W NEO",
          "Id": 555
        },
        {
          "Descricao": "JINKO MONO 475W NEO",
          "Id": 558
        },
        {
          "Descricao": "JINKO MONO 555W",
          "Id": 596
        },
        {
          "Descricao": "OSDA 585W BIF N-TYPE",
          "Id": 624
        }
      ]
    },
    {
      "Descricao": "ESTRUTURA",
      "Id": 7,
      "Valores": [
        {
          "Descricao": "LAJE TRIANGULO",
          "Id": 140
        },
        {
          "Descricao": "PARAFUSO ESTRUTURAL FERRO",
          "Id": 156
        },
        {
          "Descricao": "PARAFUSO ESTRUTURAL MADEIRA",
          "Id": 141
        },
        {
          "Descricao": "SEM ESTRUTURA",
          "Id": 132
        },
        {
          "Descricao": "SOLO",
          "Id": 133
        },
        {
          "Descricao": "TELHA COLONIAL GANCHO",
          "Id": 131
        },
        {
          "Descricao": "TELHA METALICA PERFIL 55CM",
          "Id": 143
        },
        {
          "Descricao": "TELHA ONDULADA",
          "Id": 120
        }
      ]
    },
    {
      "Descricao": "BATERIA",
      "Id": 13,
      "Valores": [
        {
          "Descricao": "SEM BATERIA",
          "Id": 350
        },
        {
          "Descricao": "UNIPOWER LITIO",
          "Id": 593
        }
      ]
    },
    {
      "Descricao": "BOMBA DA AGUA SOLAR",
      "Id": 208,
      "Valores": [
        {
          "Descricao": "GRUNDFOS",
          "Id": 536
        }
      ]
    },
    {
      "Descricao": "TIPO PRODUTO",
      "Id": 1,
      "Valores": [
        {
          "Descricao": "GERADOR",
          "Id": 23
        }
      ]
    }
  ]
};

interface Product {
  Codigo: number;
  Nome: string;
  Potencia: number;
  Preco: number;
  Estoque: number;
  marcaInversor?: string;
  modeloInversor?: string;
  nFases?: number;
  tensao?: string;
  nMPPT?: number;
  quantidadePlacas?: number;
  potenciaPlaca?: number;
  marcaPlaca?: string;
  modeloPlaca?: string;
}

interface SelectedKit {
  codigo: number | string;
  nome: string;
  potencia: number;
  preco: number;
  quantidade: number;
  insumos?: Insumo[];
  tipo?: 'aldo' | 'manual'; // Tipo do kit
  // Campos específicos para kit manual
  manualData?: {
    marcaPlaca: string;
    modeloPlaca: string;
    quantidadePlacas: number;
    marcaInversor: string;
    modeloInversor: string;
    quantidadeInversores: number;
    /** Obrigatório para leads “Sistema Solar Híbrido (com bateria)”. */
    marcaBateria?: string;
    modeloBateria?: string;
    quantidadeBaterias?: number;
    tipoEstrutura: string;
    precoTotal: number;
  };
}

/** Monta o JSON de insumos persistido para kits manuais (painel, inversor, bateria se híbrido, estrutura). */
function buildManualKitInsumosJson(
  kit: SelectedKit,
  modulosAt: ModuloFotovoltaico[],
  inversoresAt: Inversor[],
  bateriasAt: Bateria[],
  hybridLead: boolean
): { Insumos: Array<Record<string, unknown>> } {
  const md = kit.manualData;
  if (!md) {
    return { Insumos: [] };
  }
  const placaSelecionada = modulosAt.find((m) => m.id === md.modeloPlaca);
  const inversorSelecionado = inversoresAt.find((i) => i.id === md.modeloInversor);
  const insumos: Array<Record<string, unknown>> = [
    {
      Tipo: "PAINEL",
      Descricao: `${placaSelecionada?.marca} ${placaSelecionada?.modelo}`,
      Quantidade: md.quantidadePlacas,
      Potencia: (placaSelecionada?.potencia || 0) / 1000,
      Codigo: placaSelecionada?.id,
    },
    {
      Tipo: "INVERSOR",
      Descricao: `${inversorSelecionado?.marca} ${inversorSelecionado?.modelo}`,
      Quantidade: md.quantidadeInversores,
      Potencia: (inversorSelecionado?.potenciaNominal || 0) / 1000,
      Codigo: inversorSelecionado?.id,
    },
  ];
  if (hybridLead && md.modeloBateria) {
    const bateriaSelecionada = bateriasAt.find((b) => b.id === md.modeloBateria);
    insumos.push({
      Tipo: "BATERIA",
      Descricao: `${bateriaSelecionada?.marca} ${bateriaSelecionada?.modelo}`,
      Quantidade: md.quantidadeBaterias ?? 1,
      Codigo: bateriaSelecionada?.id,
    });
  }
  insumos.push({
    Tipo: "ESTRUTURA",
    Descricao: md.tipoEstrutura,
    Quantidade: 1,
  });
  return { Insumos: insumos };
}

/** Arquitetura de rede conforme cadastro em `inversores` (ausente = on-grid). */
function inversorArquiteturaRede(inv: Inversor): "ongrid" | "hibrido" {
  return inv.arquiteturaRede === "hibrido" ? "hibrido" : "ongrid";
}

/** Inversores permitidos no kit manual: híbrido só para lead híbrido; caso contrário só on-grid. */
function filterInversoresForManualKit(all: Inversor[], leadSolarHibrido: boolean): Inversor[] {
  return all.filter((inv) =>
    leadSolarHibrido ? inversorArquiteturaRede(inv) === "hibrido" : inversorArquiteturaRede(inv) === "ongrid"
  );
}

interface Step3EscolhaKitProps {
  onBack: () => void;
  onNext: (dimensionamentoId?: string) => void;
  onFinish?: () => void;
  selectedLead?: {
    id: string;
    produto?: string | null;
    nome?: string;
    razaoSocial?: string;
    grupo_tarifario?: string;
    padrao?: string;
    cidade?: string;
    estado?: string;
    tipoFixacao?: string;
    tipoTelha?: string;
  } | null;
  onKitsChanged?: () => void; // Nova prop para notificar mudanças nos kits
  dimensionamentoId?: string | null; // ID do dimensionamento selecionado
  readOnly?: boolean; // Parceiro pai visualizando lead da rede
  isParceiro?: boolean; // Parceiro concluindo dimensionamento até etapa 3
}

interface DimensionamentoData {
  systemPowerKWp: string;
  annualGenPerKwp: string;
  tipoFixacao?: string;
  tipoTelha?: string;
  padrao?: string;
  grupoTarifario?: string;
  consumoEfetivoAnual?: string;
}

export default function Step3EscolhaKit({ onBack, onNext, onFinish, selectedLead, onKitsChanged, dimensionamentoId, readOnly = false, isParceiro = false }: Step3EscolhaKitProps) {
  const isLeadSolarHibrido = isProdutoLeadSolarHibrido(selectedLead?.produto);
  const { toast } = useToast();
  const { user } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productInputs, setProductInputs] = useState<Record<string, unknown> | null>(null);
  const [showInputs, setShowInputs] = useState(false);
  const [dimensionamentoData, setDimensionamentoData] = useState<DimensionamentoData | null>(null);
  
  // Estados para controle de salvamento e mudanças
  const [isDataSaved, setIsDataSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalKits, setOriginalKits] = useState<SelectedKit[]>([]);
  const [descontoKits, setDescontoKits] = useState<number>(0);
  const [originalDescontoKits, setOriginalDescontoKits] = useState<number>(0);
  const [isEnviandoRevisao, setIsEnviandoRevisao] = useState(false);
  const [statusRevisao, setStatusRevisao] = useState<'nao_enviada' | 'aguardando_revisao' | 'revisada'>('nao_enviada');
  
  // Debug: Log quando descontoKits muda
  useEffect(() => {
    console.log('🔧 descontoKits mudou para:', descontoKits);
  }, [descontoKits]);
  
  // Kits selecionados - movido para cima para evitar erro de hoisting
  const [selectedKits, setSelectedKits] = useState<SelectedKit[]>([]);

  /** Catálogos usados em `loadExistingProposta` e no kit manual — declarados antes do `useCallback` da proposta. */
  const [modulos, setModulos] = useState<ModuloFotovoltaico[]>([]);
  const [inversores, setInversores] = useState<Inversor[]>([]);
  const [baterias, setBaterias] = useState<Bateria[]>([]);
  const [catalogsLoaded, setCatalogsLoaded] = useState(false);

  const inversoresKitManual = useMemo(
    () => filterInversoresForManualKit(inversores, isLeadSolarHibrido),
    [inversores, isLeadSolarHibrido]
  );
  
  // Função helper para determinar se o tipo de fixação é telhado
  const isTelhadoFixacao = (tipoFixacao: string): boolean => {
    return tipoFixacao === "Telhado";
  };
  
  // Função para carregar dados do dimensionamento - movida para cima para evitar erro de hoisting
  const loadDimensionamentoData = useCallback(async () => {
    if (!selectedLead?.id) {
      console.log('❌ selectedLead.id não disponível');
      return;
    }
    
    if (!dimensionamentoId) {
      console.log('❌ dimensionamentoId não disponível:', dimensionamentoId);
      return;
    }
    
    console.log('🔄 Carregando dados do dimensionamento para lead:', selectedLead.id);
    console.log('📊 selectedLead completo:', selectedLead);
    console.log('🆔 dimensionamentoId:', dimensionamentoId);
    
    try {
      // Verificar configuração do Supabase
      if (!checkSupabaseConfig()) {
        console.error('❌ Configuração do Supabase inválida');
        return;
      }
      
      // Testar conectividade
      const isConnected = await testSupabaseConnection();
      if (!isConnected) {
        console.error('❌ Não foi possível conectar ao Supabase');
        return;
      }
      
      // Buscar dados atualizados do lead com retry automático
      console.log('🔄 Buscando dados atualizados do lead...');
      
      const leadData = await withRetry(async () => {
        const { data, error } = await supabase
          .from('leads')
          .select('grupo_tarifario, padrao, tipo_fixacao, tipo_telha')
          .eq('id', selectedLead.id)
          .single();
        
        if (error) {
          console.error('❌ Erro ao carregar dados do lead:', error);
          console.error('❌ Detalhes do erro:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(`Erro ao carregar lead: ${error.message}`);
        }
        
        return data;
      });
      
      console.log('✅ Dados do lead carregados:', leadData);
      
      // Usar dados do Supabase em vez dos dados do selectedLead
      const grupoTarifario = (leadData as unknown as Record<string, unknown>).grupo_tarifario;
      const padrao = (leadData as unknown as Record<string, unknown>).padrao;
      const tipoFixacao = (leadData as unknown as Record<string, unknown>).tipo_fixacao;
      const tipoTelha = (leadData as unknown as Record<string, unknown>).tipo_telha;
      
      console.log('📊 Grupo tarifário:', grupoTarifario);
      console.log('📊 Padrão:', padrao);
      console.log('🔧 Tipo de fixação:', tipoFixacao);
      console.log('🏠 Tipo de telha:', tipoTelha);
      
      // Determinar a tabela baseada no grupo tarifário
      let targetTable;
      switch (grupoTarifario) {
        case 'Grupo A':
          targetTable = 'clientes_grupoa';
          break;
        case 'Grupo B':
          targetTable = 'clientes_grupob';
          break;
        case 'Mercado Livre':
          targetTable = 'clientes_ml';
          break;
        default:
          console.log('❌ Grupo tarifário não reconhecido:', grupoTarifario);
          return;
      }

      console.log('📋 Tabela de dimensionamento:', targetTable);

      // Buscar dados de dimensionamento da tabela específica
      let selectColumns = 'dimensio, geracao_anual_kwp';
      
      // Para Grupo B, buscar consumo_anual; para outros, manter consumo_efetivo_anual
      if (grupoTarifario === 'Grupo B') {
        selectColumns += ', consumo_anual';
      } else {
        selectColumns += ', consumo_efetivo_anual';
      }
      
      const { data: dimensionamentoDataArray, error: dimensionamentoError } = await supabase
        .from(targetTable as string)
        .select(selectColumns)
        .eq('lead_id', selectedLead.id)
        .eq('dimensionamento_id', dimensionamentoId)
        .single();
      
      const dimensionamentoData = dimensionamentoDataArray;
      
      if (dimensionamentoError) {
        console.error('❌ Erro ao carregar dados do dimensionamento:', dimensionamentoError);
        return;
      }
      
      console.log('✅ Dados de dimensionamento carregados:', dimensionamentoData);
      
      if (dimensionamentoData) {
        const dataToSet = {
          systemPowerKWp: (dimensionamentoData as unknown as Record<string, unknown>).dimensio?.toString() || '0',
          annualGenPerKwp: (dimensionamentoData as unknown as Record<string, unknown>).geracao_anual_kwp?.toString() || '0',
          tipoFixacao: (tipoFixacao as string) || '',
          tipoTelha: (tipoTelha as string) || '',
          padrao: (padrao as string) || '',
          grupoTarifario: grupoTarifario || '',
          consumoEfetivoAnual: grupoTarifario === 'Grupo B' 
            ? (dimensionamentoData as unknown as Record<string, unknown>).consumo_anual?.toString() || '0'
            : (dimensionamentoData as unknown as Record<string, unknown>).consumo_efetivo_anual?.toString() || '0'
        };
        
        console.log('📊 Dados a serem definidos no estado:', dataToSet);
        
        setDimensionamentoData(dataToSet);
        
        // Definir potência mínima baseada no dimensionamento
        if ((dimensionamentoData as unknown as Record<string, unknown>).dimensio) {
          setPotenciaMin((dimensionamentoData as unknown as Record<string, unknown>).dimensio?.toString() || '0');
          console.log('⚡ Potência mínima definida:', (dimensionamentoData as unknown as Record<string, unknown>).dimensio.toString());
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados do dimensionamento:', error);
    }
  }, [selectedLead]);

  // Função para carregar insumos de todos os kits automaticamente
  const loadInsumosForAllKits = useCallback(async (kits: SelectedKit[]) => {
    if (!token) {
      console.log('❌ Token não disponível para carregar insumos automaticamente');
      return;
    }
    
    console.log('🔄 Iniciando carregamento automático de insumos para', kits.length, 'kits');
    
    for (const kit of kits) {
      try {
        console.log(`🔄 Carregando insumos para kit ${kit.codigo}...`);
        
        const response = await fetch('/api/aldo/products-inputs-python', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, codigoProduto: kit.codigo.toString() }),
        });
        
        const data = await response.json();
        
        if (data && !data.error) {
          // Atualizar kit com insumos
          const updatedKits = selectedKits.map(k => 
            k.codigo === kit.codigo ? { ...k, insumos: data } : k
          );
          setSelectedKits(updatedKits);
          
          console.log(`✅ Insumos carregados com sucesso para kit ${kit.codigo}`);
        } else {
          // Verificar se é erro de token expirado
          if (data.error && data.error.includes('401')) {
            console.log(`🔄 Token expirado para kit ${kit.codigo}, tentando renovar...`);
            const tokenRenovado = await regenerateToken();
            if (tokenRenovado) {
              // Tentar novamente com o novo token
              const retryResponse = await fetch('/api/aldo/products-inputs-python', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, codigoProduto: kit.codigo.toString() }),
              });
              
              const retryData = await retryResponse.json();
              
              if (retryData && !retryData.error) {
                // Atualizar kit com insumos
                const updatedKits = selectedKits.map(k => 
                  k.codigo === kit.codigo ? { ...k, insumos: retryData } : k
                );
                setSelectedKits(updatedKits);
                
                console.log(`✅ Insumos carregados com sucesso para kit ${kit.codigo} (token renovado)`);
              } else {
                console.error(`❌ Erro ao carregar insumos para kit ${kit.codigo} após renovar token:`, retryData.error);
              }
            } else {
              console.error(`❌ Falha ao renovar token para kit ${kit.codigo}`);
            }
          } else {
            console.error(`❌ Erro ao carregar insumos para kit ${kit.codigo}:`, data.error);
          }
        }
      } catch (error) {
        console.error(`❌ Erro ao carregar insumos para kit ${kit.codigo}:`, error);
      }
    }
    
    console.log('✅ Carregamento automático de insumos concluído');
  }, [token, selectedKits]);

  // Função para carregar proposta existente - movida para cima para evitar erro de hoisting
  const loadExistingProposta = useCallback(async () => {
    if (!selectedLead?.id || !dimensionamentoId) return;
    
    try {
      console.log('🔄 Carregando proposta existente para lead:', selectedLead.id, 'dimensionamento:', dimensionamentoId);
      
      const data = await withRetry(async () => {
        const { data, error } = await supabase
          .from('propostas_clientes')
          .select('*')
          .eq('lead_id', selectedLead.id)
          .eq('dimensionamento_id', dimensionamentoId)
          .single();
        
        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
          console.error('Erro ao carregar proposta existente:', error);
          console.error('❌ Detalhes do erro:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(`Erro ao carregar proposta: ${error.message}`);
        }
        
        return data;
      });
      
      if (data) {
        // Converter dados da proposta para selectedKits
        const kits: SelectedKit[] = [];
        
        // Função helper para processar um kit
        const processKit = (kitData: any, kitNum: number): SelectedKit | null => {
          const codigo = kitData[`kit${kitNum}_codigo`];
          if (!codigo) return null;
          
          const isManual = typeof codigo === 'string' && codigo.startsWith('manual-');
          
          let manualData = null;
          if (isManual && kitData[`kit${kitNum}_insumos`]) {
            // Extrair dados do kit manual dos insumos salvos
            const insumos = kitData[`kit${kitNum}_insumos`];
            if (insumos && insumos.Insumos && Array.isArray(insumos.Insumos)) {
              const placaInsumo = insumos.Insumos.find((i: any) => i.Tipo === 'PAINEL' || i.Tipo === 'Painel Solar');
              const inversorInsumo = insumos.Insumos.find((i: any) => i.Tipo === 'INVERSOR' || i.Tipo === 'Inversor');
              const estruturaInsumo = insumos.Insumos.find((i: any) => i.Tipo === 'ESTRUTURA' || i.Tipo === 'Estrutura');
              const bateriaInsumo = insumos.Insumos.find(
                (i: any) => i.Tipo === 'BATERIA' || i.Tipo === 'Bateria'
              );
              
              if (placaInsumo && inversorInsumo && estruturaInsumo) {
                // Tentar encontrar módulo e inversor pelo ID primeiro
                let modulo = modulos.find(m => m.id === placaInsumo.Codigo);
                let inversor = inversores.find(i => i.id === inversorInsumo.Codigo);

                // Fallback por descrição: usar tudo após a marca (ex.: "Leapton LP182*210-M-66-NB 620W")
                // para não colidir com outras potências do mesmo prefixo (610W–640W).
                if (!modulo) {
                  const placaNome = String(placaInsumo.Descricao || '').trim();
                  const partesPlaca = placaNome.split(/\s+/);
                  const marcaPlaca = partesPlaca[0] || '';
                  const modeloResto = partesPlaca.slice(1).join(' ').trim();
                  modulo = modulos.find(
                    (m) => m.marca === marcaPlaca && modeloResto !== '' && m.modelo === modeloResto
                  );
                }

                if (!inversor) {
                  const inversorNome = inversorInsumo.Descricao || '';
                  const [marcaInversor, modeloInversor] = inversorNome.split(' ').slice(0, 2);
                  inversor = inversores.find(i => i.marca === marcaInversor && i.modelo.includes(modeloInversor));
                }

                let bateriaCatalogo: Bateria | null = null;
                if (bateriaInsumo) {
                  bateriaCatalogo = baterias.find((b) => b.id === bateriaInsumo.Codigo) ?? null;
                  if (!bateriaCatalogo) {
                    const nomeBat = String(bateriaInsumo.Descricao || "").trim();
                    if (nomeBat) {
                      const partes = nomeBat.split(/\s+/);
                      const marcaBat = partes[0] ?? "";
                      const resto = partes.slice(1).join(" ").trim();
                      const primeiroModelo = resto.split(/\s+/)[0] ?? "";
                      bateriaCatalogo =
                        baterias.find(
                          (b) =>
                            b.marca === marcaBat &&
                            (primeiroModelo === "" || b.modelo.includes(primeiroModelo))
                        ) ?? null;
                    }
                  }
                }
                
                if (modulo && inversor) {
                  manualData = {
                    marcaPlaca: modulo.marca,
                    modeloPlaca: modulo.id,
                    quantidadePlacas: placaInsumo.Quantidade || 1,
                    marcaInversor: inversor.marca,
                    modeloInversor: inversor.id,
                    quantidadeInversores: inversorInsumo.Quantidade || 1,
                    tipoEstrutura: estruturaInsumo.Descricao || '',
                    precoTotal: typeof kitData[`kit${kitNum}_preco`] === 'number' ? kitData[`kit${kitNum}_preco`] : 0
                  };
                  if (bateriaCatalogo) {
                    manualData.marcaBateria = bateriaCatalogo.marca;
                    manualData.modeloBateria = bateriaCatalogo.id;
                    manualData.quantidadeBaterias = bateriaInsumo?.Quantidade || 1;
                  }
                }
              }
            }
          }
          
          return {
            codigo,
            nome: kitData[`kit${kitNum}_nome`] || '',
            potencia: typeof kitData[`kit${kitNum}_potencia`] === 'number' ? kitData[`kit${kitNum}_potencia`] : 0,
            preco: typeof kitData[`kit${kitNum}_preco`] === 'number' ? kitData[`kit${kitNum}_preco`] : 0,
            quantidade: typeof kitData[`kit${kitNum}_quantidade`] === 'number' ? kitData[`kit${kitNum}_quantidade`] : 1,
            insumos: kitData[`kit${kitNum}_insumos`] || null,
            tipo: isManual ? 'manual' : 'aldo',
            manualData
          };
        };
        
        const kit1 = processKit(data, 1);
        if (kit1) kits.push(kit1);
        
        const kit2 = processKit(data, 2);
        if (kit2) kits.push(kit2);
        
        const kit3 = processKit(data, 3);
        if (kit3) kits.push(kit3);
        
        const kit4 = processKit(data, 4);
        if (kit4) kits.push(kit4);
        
        setSelectedKits(kits);
        
        // Carregar desconto_kits se existir
        const descontoCarregado = (data.desconto_kits !== undefined && data.desconto_kits !== null) 
          ? Number(data.desconto_kits) 
          : 0;
        setDescontoKits(descontoCarregado);
        setOriginalDescontoKits(descontoCarregado);
        
        // Definir kits originais para detecção de mudanças
        setOriginalKits(kits.map(kit => ({ ...kit }))); // Cópia profunda
        setIsDataSaved(true);
        setHasUnsavedChanges(false);
        
        // Log dos kits carregados
        console.log('📊 Kits carregados da proposta existente:', kits.length);
        console.log('💾 Kits originais definidos para detecção de mudanças');
        
        // Verificar se os kits já têm insumos carregados
        const kitsComInsumos = kits.filter(kit => kitTemInsumos(kit));
        
        // Carregar insumos automaticamente apenas para kits que não os têm
        if (kitsComInsumos.length < kits.length) {
          const kitsSemInsumos = kits.filter(kit => !kitTemInsumos(kit));
          
          console.log(`🔄 Carregando insumos automaticamente para ${kitsSemInsumos.length} kits sem insumos...`);
          await loadInsumosForAllKits(kitsSemInsumos);
        } else {
          console.log('✅ Todos os kits já têm insumos carregados');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar proposta existente:', error);
    }
  }, [selectedLead, dimensionamentoId, modulos, inversores, baterias]);

  // Função helper para verificar se um kit tem insumos válidos
  const kitTemInsumos = (kit: SelectedKit): boolean => {
    console.log('🚀 FUNÇÃO kitTemInsumos CHAMADA - Kit:', kit.codigo);
    // Kits manuais sempre têm insumos (criados quando o kit é adicionado)
    if (kit.tipo === 'manual' && kit.manualData) {
      return true;
    }
    if (!kit.insumos) return false;
    
    console.log('🔍 kitTemInsumos - Verificando kit:', kit.codigo);
    console.log('📦 Kit insumos completo:', kit.insumos);
    
    // Verificar se kit.insumos é um array
    if (Array.isArray(kit.insumos)) {
      if (kit.insumos.length === 0) return false;
      
      // Verificar se tem estrutura Insumos (array) ou Data (objeto único)
      const firstInsumo = kit.insumos[0];
      if (firstInsumo) {
        console.log('🔍 Primeiro insumo:', firstInsumo);
        console.log('🔍 Propriedades do primeiro insumo:', Object.keys(firstInsumo));
        
        // Verificar se tem a propriedade Insumos (qualquer tipo)
        if ((firstInsumo as any).Insumos) {
          console.log('✅ Encontrou propriedade Insumos:', (firstInsumo as any).Insumos);
          return true;
        }
        // Verificar se tem a propriedade Data
        if ((firstInsumo as any).Data) {
          console.log('✅ Encontrou propriedade Data:', (firstInsumo as any).Data);
          return true;
        }
        // Verificar se o próprio objeto tem propriedades de insumo (estrutura direta)
        if ((firstInsumo as any).Tipo || (firstInsumo as any).tipo || (firstInsumo as any).Potencia || (firstInsumo as any).potencia) {
          console.log('✅ Encontrou propriedades de insumo diretas');
          return true;
        }
        
        console.log('❌ Nenhuma estrutura válida encontrada');
      }
    } else {
      // kit.insumos é um objeto único (estrutura correta do Supabase)
      console.log('🔍 Kit insumos é um objeto único:', kit.insumos);
      console.log('🔍 Propriedades do objeto insumos:', Object.keys(kit.insumos));
      
      // Verificar se tem a propriedade Insumos (array de insumos)
      if ((kit.insumos as any).Insumos && Array.isArray((kit.insumos as any).Insumos)) {
        console.log('✅ Encontrou propriedade Insumos (array) no objeto:', (kit.insumos as any).Insumos.length, 'itens');
        return (kit.insumos as any).Insumos.length > 0;
      }
      // Verificar se tem a propriedade Data
      if ((kit.insumos as any).Data) {
        console.log('✅ Encontrou propriedade Data no objeto:', (kit.insumos as any).Data);
        return true;
      }
      // Verificar se o próprio objeto tem propriedades de insumo (estrutura direta)
      if ((kit.insumos as any).Tipo || (kit.insumos as any).tipo || (kit.insumos as any).Potencia || (kit.insumos as any).potencia) {
        console.log('✅ Encontrou propriedades de insumo diretas no objeto');
        return true;
      }
      
      console.log('❌ Nenhuma estrutura válida encontrada no objeto');
    }
    return false;
  };

  // Função para limpar dados da etapa 4 quando há mudanças nos kits
  const clearEtapa4Data = useCallback(async () => {
    if (!selectedLead?.id || !dimensionamentoId) return;
    
    try {
      console.log('🧹 Limpando dados da etapa 4 devido a mudanças nos kits...');
      
      // Limpar dados de precificação da tabela propostas_clientes
      const { error } = await supabase
        .from('propostas_clientes')
        .update({
          valor_estrutura_metallica: null,
          valor_fundacao: null,
          valor_equipamentos_alugados: null,
          custo_materiais: null,
          comissao_vendedor: null,
          comissao_parceiro: null,
          comissao_vendedor_valor: null,
          comissao_parceiro_valor: null,
          integracao_r_wp: null,
          impostos_percentual: null,
          mao_obra_por_placa: null,
          preco_projeto: null,
          preco_integracao: null,
          preco_estrutura_metallica_calc: null,
          impostos: null,
          mao_obra: null,
          custo_total_implantacao: null,
          custo_total_integracao: null,
          preco_por_wp_final: null,
          lucro_liquido: null,
          lucro_liquido_percentual: null
        })
        .eq('lead_id', selectedLead.id)
        .eq('dimensionamento_id', dimensionamentoId);
      
      if (error) {
        console.error('❌ Erro ao limpar dados da etapa 4:', error);
      } else {
        console.log('✅ Dados da etapa 4 limpos com sucesso');
        
        // Notificar o componente pai sobre a mudança
        if (onKitsChanged) {
          onKitsChanged();
        }
        
        toast({
          title: 'Dados da Etapa 4 Limpos',
          description: 'Os dados de precificação foram limpos devido a mudanças nos kits. Será necessário refazer a etapa 4.',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('❌ Erro ao limpar dados da etapa 4:', error);
    }
  }, [selectedLead?.id, dimensionamentoId, onKitsChanged, toast]);

  // Função para detectar mudanças nos kits
  const detectChanges = useCallback(() => {
    if (!originalKits || originalKits.length === 0) {
      return false;
    }

    // Função auxiliar para normalizar valores para comparação
    const normalizeValue = (value: unknown): string => {
      if (value === null || value === undefined || value === "") return "";
      return String(value).trim();
    };

    // Função auxiliar para comparar arrays de kits
    const compareKits = (current: SelectedKit[], original: SelectedKit[]): boolean => {
      if (!Array.isArray(current) || !Array.isArray(original)) {
        console.log('🔍 compareKits: Arrays inválidos', { current, original });
        return false;
      }
      if (current.length !== original.length) {
        console.log('🔍 compareKits: Tamanhos diferentes', { currentLength: current.length, originalLength: original.length });
        return true;
      }
      
      for (let i = 0; i < current.length; i++) {
        const currentKit = current[i] || {};
        const originalKit = original[i] || {};
        
        // Comparar campos básicos do kit
        const fields = ['codigo', 'nome', 'potencia', 'preco', 'quantidade'];
        for (const field of fields) {
          const currentVal = normalizeValue(currentKit[field]);
          const originalVal = normalizeValue(originalKit[field]);
          if (currentVal !== originalVal) {
            console.log(`🔍 Mudança detectada no kit ${i}, campo ${field}:`, { 
              current: currentVal, 
              original: originalVal,
              currentKit,
              originalKit
            });
            return true;
          }
        }
      }
      console.log('🔍 compareKits: Nenhuma mudança detectada');
      return false;
    };

    const kitsChanged = compareKits(selectedKits, originalKits);
    
    // Verificar mudanças no desconto
    const descontoChanged = descontoKits !== originalDescontoKits;
    if (descontoChanged) {
      console.log('🔍 Mudança detectada no desconto:', {
        atual: descontoKits,
        original: originalDescontoKits
      });
    }
    
    const hasChanges = kitsChanged || descontoChanged;

    console.log('🔍 detectChanges resultado:', {
      kitsChanged,
      descontoChanged,
      hasChanges,
      selectedKitsLength: selectedKits.length,
      originalKitsLength: originalKits.length
    });

    setHasUnsavedChanges(hasChanges);

    if (hasChanges) {
      setIsDataSaved(false);
    }

    return hasChanges;
  }, [selectedKits, originalKits, descontoKits, originalDescontoKits]);

  // Função para verificar se pode prosseguir para a próxima etapa
  const canProceedToNext = () => {
    // Não pode prosseguir se não há kits selecionados
    if (selectedKits.length === 0) {
      return false;
    }
    
    // Não pode prosseguir se os dados não foram salvos
    if (!isDataSaved) {
      return false;
    }
    
    // Não pode prosseguir se há alterações não salvas
    if (hasUnsavedChanges) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!isParceiro || !selectedLead?.id || !dimensionamentoId) {
      return;
    }

    const loadStatusRevisao = async () => {
      const { data, error } = await supabase
        .from('propostas_clientes')
        .select('status_revisao')
        .eq('lead_id', selectedLead.id)
        .eq('dimensionamento_id', dimensionamentoId)
        .maybeSingle();

      if (!error && data?.status_revisao) {
        setStatusRevisao(data.status_revisao as 'nao_enviada' | 'aguardando_revisao' | 'revisada');
      }
    };

    loadStatusRevisao();
  }, [isParceiro, selectedLead?.id, dimensionamentoId]);

  const enviarParaPrecificacao = useCallback(async () => {
    if (!selectedLead?.id || !dimensionamentoId) {
      toast({
        title: "Erro",
        description: "Lead ou dimensionamento não selecionado.",
        variant: "destructive"
      });
      return;
    }

    if (!isDataSaved || hasUnsavedChanges) {
      toast({
        title: "Atenção",
        description: "Salve os kits antes de enviar para precificação.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsEnviandoRevisao(true);

      const { data: propostaData, error: fetchError } = await supabase
        .from('propostas_clientes')
        .select('id, status_revisao')
        .eq('lead_id', selectedLead.id)
        .eq('dimensionamento_id', dimensionamentoId)
        .single();

      if (fetchError || !propostaData) {
        throw new Error(fetchError?.message || 'Proposta não encontrada');
      }

      const { data, error } = await supabase
        .rpc('enviar_proposta_parceiro_para_revisao' as never, {
          p_proposta_id: propostaData.id,
          p_parceiro_id: user?.id
        } as never);

      if (error) {
        throw new Error(error.message);
      }

      const result = data as unknown as { success: boolean; message?: string; error?: string; lead_nome?: string };
      if (!result.success) {
        throw new Error(result.error || 'Erro ao enviar para precificação');
      }

      setStatusRevisao('aguardando_revisao');

      toast({
        title: "Enviado para Precificação",
        description: `O dimensionamento do cliente ${result.lead_nome || selectedLead.nome || selectedLead.razaoSocial} foi enviado para precificação pelo administrador.`,
        duration: 5000
      });

      setTimeout(() => {
        onFinish?.();
      }, 2000);
    } catch (error) {
      console.error('Erro ao enviar para precificação:', error);
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao enviar dimensionamento para precificação.",
        variant: "destructive"
      });
    } finally {
      setIsEnviandoRevisao(false);
    }
  }, [selectedLead, dimensionamentoId, isDataSaved, hasUnsavedChanges, user?.id, toast, onFinish]);
  
  // Filter states
  const [potenciaMin, setPotenciaMin] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<Record<number, string>>({});
  
  const [showKitSelection, setShowKitSelection] = useState(false);
  const [kitToAdd, setKitToAdd] = useState<Product | null>(null);
  const [kitQuantity, setKitQuantity] = useState<number>(1);
  
  // Estados para tipo de kit (Aldo ou Manual)
  const [kitType, setKitType] = useState<'aldo' | 'manual' | null>(null);
  
  // Estados para kit manual (módulos/inversores/baterias declarados acima)
  const [marcasPlacas, setMarcasPlacas] = useState<string[]>([]);
  const [marcasInversores, setMarcasInversores] = useState<string[]>([]);
  const [marcasBaterias, setMarcasBaterias] = useState<string[]>([]);
  const [modelosPlacas, setModelosPlacas] = useState<ModuloFotovoltaico[]>([]);
  const [modelosInversores, setModelosInversores] = useState<Inversor[]>([]);
  const [modelosBaterias, setModelosBaterias] = useState<Bateria[]>([]);
  
  // Estados para formulário de kit manual
  const [manualKitData, setManualKitData] = useState({
    marcaPlaca: '',
    modeloPlaca: '',
    quantidadePlacas: 0, // Valor numérico (será usado apenas para cálculos)
    marcaInversor: '',
    modeloInversor: '',
    quantidadeInversores: 0, // Valor numérico (será usado apenas para cálculos)
    marcaBateria: '',
    modeloBateria: '',
    quantidadeBaterias: 0,
    tipoEstrutura: '',
    precoTotal: 0
  });
  
  // Estados de edição para campos numéricos (permitem edição livre)
  const [editingQuantidadePlacas, setEditingQuantidadePlacas] = useState<string>('');
  const [editingQuantidadeInversores, setEditingQuantidadeInversores] = useState<string>('');
  const [editingQuantidadeBaterias, setEditingQuantidadeBaterias] = useState<string>('');
  
  // Resetar estados de edição quando o kit manual é selecionado
  useEffect(() => {
    if (kitType === 'manual') {
      setEditingQuantidadePlacas('');
      setEditingQuantidadeInversores('');
      setEditingQuantidadeBaterias('');
      setManualKitData(prev => ({
        ...prev,
        quantidadePlacas: 0,
        quantidadeInversores: 0,
        quantidadeBaterias: 0
      }));
    }
  }, [kitType]);
  
  // Carregar módulos, inversores e baterias (mesma origem das páginas de catálogo)
  useEffect(() => {
    let cancelled = false;
    const loadCatalogs = async () => {
      setCatalogsLoaded(false);
      try {
        const [modulosData, inversoresData, bateriasData] = await Promise.all([
          modulosService.getAll(),
          inversoresService.getAll(),
          bateriasService.getAll(),
        ]);
        if (cancelled) return;
        setModulos(modulosData);
        setInversores(inversoresData);
        setBaterias(bateriasData);
        const marcasPlacasUnicas = [...new Set(modulosData.map((m) => m.marca))].sort();
        const marcasBateriasUnicas = [...new Set(bateriasData.map((b) => b.marca))].sort();
        setMarcasPlacas(marcasPlacasUnicas);
        setMarcasBaterias(marcasBateriasUnicas);
      } catch (error) {
        console.error('Erro ao carregar catálogos (módulos, inversores, baterias):', error);
        if (!cancelled) {
          toast({
            title: 'Erro',
            description: 'Não foi possível carregar módulos, inversores ou baterias',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setCatalogsLoaded(true);
      }
    };
    void loadCatalogs();
    return () => {
      cancelled = true;
    };
  }, []);

  // Marcas de inversor no kit manual conforme arquitetura (on-grid vs híbrido) do lead
  useEffect(() => {
    setMarcasInversores([...new Set(inversoresKitManual.map((i) => i.marca))].sort());
  }, [inversoresKitManual]);

  // Atualizar modelos de placas quando marca for selecionada
  useEffect(() => {
    if (manualKitData.marcaPlaca) {
      const modelos = modulos.filter(m => m.marca === manualKitData.marcaPlaca);
      setModelosPlacas(modelos);
      // Resetar modelo selecionado se não estiver mais disponível
      if (manualKitData.modeloPlaca && !modelos.find(m => m.id === manualKitData.modeloPlaca)) {
        setManualKitData(prev => ({ ...prev, modeloPlaca: '' }));
      }
    } else {
      setModelosPlacas([]);
    }
  }, [manualKitData.marcaPlaca, modulos]);

  // Atualizar modelos de inversores quando marca for selecionada (catálogo filtrado por produto do lead)
  useEffect(() => {
    if (manualKitData.marcaInversor) {
      const modelos = inversoresKitManual.filter(i => i.marca === manualKitData.marcaInversor);
      setModelosInversores(modelos);
      // Resetar modelo selecionado se não estiver mais disponível
      if (manualKitData.modeloInversor && !modelos.find(i => i.id === manualKitData.modeloInversor)) {
        setManualKitData(prev => ({ ...prev, modeloInversor: '' }));
      }
    } else {
      setModelosInversores([]);
    }
  }, [manualKitData.marcaInversor, inversoresKitManual]);

  // Se o catálogo ou o produto do lead mudar, limpar inversor fora da arquitetura permitida
  useEffect(() => {
    setManualKitData((prev) => {
      const marcaOk = !prev.marcaInversor || inversoresKitManual.some((i) => i.marca === prev.marcaInversor);
      const modeloOk = !prev.modeloInversor || inversoresKitManual.some((i) => i.id === prev.modeloInversor);
      if (marcaOk && modeloOk) return prev;
      if (!marcaOk) {
        return { ...prev, marcaInversor: '', modeloInversor: '', quantidadeInversores: 0 };
      }
      return { ...prev, modeloInversor: '', quantidadeInversores: 0 };
    });
    setEditingQuantidadeInversores('');
  }, [inversoresKitManual]);

  // Modelos de bateria por marca (lead híbrido)
  useEffect(() => {
    if (manualKitData.marcaBateria) {
      const modelos = baterias.filter((b) => b.marca === manualKitData.marcaBateria);
      setModelosBaterias(modelos);
      if (manualKitData.modeloBateria && !modelos.find((b) => b.id === manualKitData.modeloBateria)) {
        setManualKitData((prev) => ({ ...prev, modeloBateria: '' }));
      }
    } else {
      setModelosBaterias([]);
    }
  }, [manualKitData.marcaBateria, baterias]);

  // Carregar dados do dimensionamento quando o componente for montado
  useEffect(() => {
    if (selectedLead?.id) {
      loadDimensionamentoData();
      if (catalogsLoaded) {
        void loadExistingProposta();
      }
    }
  }, [selectedLead?.id, loadDimensionamentoData, catalogsLoaded, loadExistingProposta]);

  // Detectar mudanças nos kits
  useEffect(() => {
    console.log('🔍 useEffect detectChanges chamado:', {
      selectedKitsLength: selectedKits.length,
      originalKitsLength: originalKits.length,
      hasOriginalKits: originalKits && originalKits.length > 0
    });
    
    if (originalKits && originalKits.length > 0) {
      console.log('🔍 Chamando detectChanges...');
      detectChanges();
    } else {
      console.log('ℹ️ Não chamando detectChanges - originalKits vazio ou inválido');
    }
  }, [selectedKits, originalKits, detectChanges]);




  // Função para calcular informações do kit manual
  const calculateManualKitInfo = () => {
    const placaSelecionada = modulos.find(m => m.id === manualKitData.modeloPlaca);
    const inversorSelecionado = inversoresKitManual.find(i => i.id === manualKitData.modeloInversor);
    
    if (!placaSelecionada || !inversorSelecionado) {
      return null;
    }
    
    // Calcular potência total dos painéis (kWp)
    const potenciaTotalPaineis = (placaSelecionada.potencia * manualKitData.quantidadePlacas) / 1000; // Converter W para kW
    
    // Calcular potência total dos inversores (kW)
    const potenciaTotalInversores = (inversorSelecionado.potenciaNominal * manualKitData.quantidadeInversores) / 1000; // Converter W para kW
    
    // Calcular R$/Wp
    const rPorWp = manualKitData.precoTotal > 0 && potenciaTotalPaineis > 0 
      ? (manualKitData.precoTotal / (potenciaTotalPaineis * 1000))
      : 0;
    
    return {
      potenciaTotalPaineis,
      potenciaTotalInversores,
      totalPaineis: manualKitData.quantidadePlacas,
      rPorWp
    };
  };

  // Função para adicionar kit manual à seleção
  const addManualKitToSelection = () => {
    if (selectedKits.length >= 4) {
      toast({
        title: 'Limite atingido',
        description: 'Você pode selecionar no máximo 4 tipos de kits diferentes',
        variant: 'destructive'
      });
      return;
    }

    // Validações
    if (!manualKitData.marcaPlaca || !manualKitData.modeloPlaca) {
      toast({
        title: 'Erro',
        description: 'Selecione a marca e modelo da placa',
        variant: 'destructive'
      });
      return;
    }

    if (!manualKitData.quantidadePlacas || manualKitData.quantidadePlacas <= 0) {
      toast({
        title: 'Erro',
        description: 'Informe a quantidade de placas (deve ser maior que zero)',
        variant: 'destructive'
      });
      return;
    }

    if (!manualKitData.marcaInversor || !manualKitData.modeloInversor) {
      toast({
        title: 'Erro',
        description: 'Selecione a marca e modelo do inversor',
        variant: 'destructive'
      });
      return;
    }

    if (!manualKitData.quantidadeInversores || manualKitData.quantidadeInversores <= 0) {
      toast({
        title: 'Erro',
        description: 'Informe a quantidade de inversores (deve ser maior que zero)',
        variant: 'destructive'
      });
      return;
    }

    if (isLeadSolarHibrido) {
      if (!manualKitData.marcaBateria || !manualKitData.modeloBateria) {
        toast({
          title: 'Erro',
          description: 'Selecione a marca e modelo da bateria',
          variant: 'destructive'
        });
        return;
      }
      if (!manualKitData.quantidadeBaterias || manualKitData.quantidadeBaterias <= 0) {
        toast({
          title: 'Erro',
          description: 'Informe a quantidade de baterias (deve ser maior que zero)',
          variant: 'destructive'
        });
        return;
      }
    }

    if (!manualKitData.tipoEstrutura) {
      toast({
        title: 'Erro',
        description: 'Selecione o tipo de estrutura',
        variant: 'destructive'
      });
      return;
    }

    if (manualKitData.precoTotal <= 0) {
      toast({
        title: 'Erro',
        description: 'Informe o preço total dos produtos',
        variant: 'destructive'
      });
      return;
    }

    const placaSelecionada = modulos.find(m => m.id === manualKitData.modeloPlaca);
    const inversorSelecionado = inversoresKitManual.find(i => i.id === manualKitData.modeloInversor);
    const bateriaSelecionada = isLeadSolarHibrido
      ? baterias.find((b) => b.id === manualKitData.modeloBateria)
      : undefined;
    
    if (!placaSelecionada || !inversorSelecionado) {
      toast({
        title: 'Erro',
        description: 'Placa ou inversor não encontrado',
        variant: 'destructive'
      });
      return;
    }

    if (isLeadSolarHibrido && !bateriaSelecionada) {
      toast({
        title: 'Erro',
        description: 'Bateria não encontrada no catálogo',
        variant: 'destructive'
      });
      return;
    }

    const kitInfo = calculateManualKitInfo();
    if (!kitInfo) {
      toast({
        title: 'Erro',
        description: 'Erro ao calcular informações do kit',
        variant: 'destructive'
      });
      return;
    }

    // Criar nome do kit manual
    const nomeKit = isLeadSolarHibrido && bateriaSelecionada
      ? `Kit Manual - ${placaSelecionada.marca} ${placaSelecionada.modelo} (${manualKitData.quantidadePlacas}x) + ${inversorSelecionado.marca} ${inversorSelecionado.modelo} (${manualKitData.quantidadeInversores}x) + ${bateriaSelecionada.marca} ${bateriaSelecionada.modelo} (${manualKitData.quantidadeBaterias}x)`
      : `Kit Manual - ${placaSelecionada.marca} ${placaSelecionada.modelo} (${manualKitData.quantidadePlacas}x) + ${inversorSelecionado.marca} ${inversorSelecionado.modelo} (${manualKitData.quantidadeInversores}x)`;

    // Criar kit manual
    const manualKit: SelectedKit = {
      codigo: `manual-${Date.now()}`, // ID único para kit manual
      nome: nomeKit,
      potencia: kitInfo.potenciaTotalPaineis,
      preco: manualKitData.precoTotal,
      quantidade: 1,
      tipo: 'manual',
      manualData: { ...manualKitData }
    };

    setSelectedKits([...selectedKits, manualKit]);
    setIsDataSaved(false);
    setHasUnsavedChanges(true);

    // Resetar formulário
    setManualKitData({
      marcaPlaca: '',
      modeloPlaca: '',
      quantidadePlacas: 0,
      marcaInversor: '',
      modeloInversor: '',
      quantidadeInversores: 0,
      marcaBateria: '',
      modeloBateria: '',
      quantidadeBaterias: 0,
      tipoEstrutura: '',
      precoTotal: 0
    });
    // Resetar estados de edição
    setEditingQuantidadePlacas('');
    setEditingQuantidadeInversores('');
    setEditingQuantidadeBaterias('');

    toast({
      title: 'Sucesso',
      description: 'Kit manual adicionado com sucesso!'
    });
  };

  // Função para adicionar kit à seleção
  const addKitToSelection = async (product: Product, quantity: number) => {
    if (selectedKits.length >= 4) {
      toast({
        title: 'Limite atingido',
        description: 'Você pode selecionar no máximo 4 tipos de kits diferentes',
        variant: 'destructive'
      });
      return;
    }

    // Verificar se o kit já existe
    const existingKitIndex = selectedKits.findIndex(kit => kit.codigo === product.Codigo);
    
    if (existingKitIndex >= 0) {
      // Atualizar quantidade do kit existente
      const updatedKits = [...selectedKits];
      updatedKits[existingKitIndex].quantidade += quantity;
      setSelectedKits(updatedKits);
      
      // Reativar botão de salvar quando há mudanças
      setIsDataSaved(false);
      
      toast({
        title: 'Kit atualizado',
        description: `Quantidade do kit "${product.Nome}" atualizada para ${updatedKits[existingKitIndex].quantidade}`
      });
    } else {
      // Adicionar novo kit
      const newKit: SelectedKit = {
        codigo: product.Codigo,
        nome: product.Nome,
        potencia: product.Potencia,
        preco: product.Preco,
        quantidade: quantity,
        insumos: null // Será preenchido quando buscar insumos
      };
      
      setSelectedKits([...selectedKits, newKit]);
      
      // Reativar botão de salvar quando há mudanças
      setIsDataSaved(false);
      
      toast({
        title: 'Kit adicionado',
        description: `Kit "${product.Nome}" adicionado à seleção`
      });
    }
    
    setShowKitSelection(false);
    setKitToAdd(null);
    setKitQuantity(1);
  };

  // Função para remover kit da seleção
  const removeKitFromSelection = async (index: number) => {
    if (!selectedLead?.id) {
      toast({
        title: 'Erro',
        description: 'Nenhum lead selecionado',
        variant: 'destructive'
      });
      return;
    }

    // Garantir que módulos, inversores e baterias estejam carregados antes de remover
    // Usar variáveis locais para garantir que temos os dados mais recentes
    let modulosAtuais = modulos;
    let inversoresAtuais = inversores;
    let bateriasAtuais = baterias;
    
    if (modulosAtuais.length === 0 || inversoresAtuais.length === 0) {
      console.log('🔄 [removeKit] Módulos ou inversores não carregados, recarregando...');
      try {
        const modulosData = await modulosService.getAll();
        const inversoresData = await inversoresService.getAll();
        const bateriasData = await bateriasService.getAll();
        modulosAtuais = modulosData;
        inversoresAtuais = inversoresData;
        bateriasAtuais = bateriasData;
        setModulos(modulosData);
        setInversores(inversoresData);
        setBaterias(bateriasData);
        console.log(`✅ [removeKit] Catálogos recarregados: ${modulosData.length} módulos, ${inversoresData.length} inversores, ${bateriasData.length} baterias`);
      } catch (error) {
        console.error('❌ [removeKit] Erro ao recarregar catálogos:', error);
      }
    }

    const kitToRemove = selectedKits[index];
    const updatedKits = selectedKits.filter((_, i) => i !== index);
    setSelectedKits(updatedKits);
    
    // Salvar a alteração no Supabase
    try {
      console.log('🔄 Removendo kit da proposta no Supabase...');
      console.log('📦 Kit a ser removido:', kitToRemove);
      console.log('📊 Kits restantes:', updatedKits.length);
      
      // Preparar dados para salvar
      const valorTotalKits = updatedKits.reduce((total, kit) => {
        const preco = typeof kit.preco === 'number' && !isNaN(kit.preco) ? kit.preco : 0;
        const quantidade = typeof kit.quantidade === 'number' && !isNaN(kit.quantidade) ? kit.quantidade : 1;
        return total + (preco * quantidade);
      }, 0);
      
      const potenciaTotalInversores = updatedKits.reduce((total, kit) => {
        return total + calculateTotalPotenciaInversores(kit);
      }, 0);
      
      const totalPaineis = updatedKits.reduce((total, kit) => {
        return total + calculateTotalPaineis(kit);
      }, 0);
      
      const propostaData = {
        lead_id: selectedLead.id,
        total_kits: updatedKits.length,
        valor_total_kits: valorTotalKits,
        potencia_total_inversores: potenciaTotalInversores,
        total_paineis: totalPaineis
      };

      // Limpar dados dos kits removidos e adicionar dados dos kits restantes
      // Primeiro, limpar todos os campos de kits
      for (let i = 1; i <= 4; i++) {
        propostaData[`kit${i}_codigo`] = null;
        propostaData[`kit${i}_nome`] = null;
        propostaData[`kit${i}_potencia`] = null;
        propostaData[`kit${i}_preco`] = null;
        propostaData[`kit${i}_quantidade`] = null;
        propostaData[`kit${i}_insumos`] = null;
        propostaData[`kit${i}_potencia_painel`] = null;
        propostaData[`kit${i}_quantidade_paineis`] = null;
        propostaData[`kit${i}_modulo_descricao`] = null;
      }

      // Adicionar dados dos kits restantes
      updatedKits.forEach((kit, kitIndex) => {
        const kitNum = kitIndex + 1;
        // Garantir que o código seja sempre uma string (suporta kits Aldo numéricos e kits manuais com strings)
        propostaData[`kit${kitNum}_codigo`] = kit.codigo ? String(kit.codigo) : null;
        propostaData[`kit${kitNum}_nome`] = kit.nome || null;
        
        const potencia = typeof kit.potencia === 'number' && !isNaN(kit.potencia) ? kit.potencia : 0;
        const preco = typeof kit.preco === 'number' && !isNaN(kit.preco) ? kit.preco : 0;
        const quantidade = typeof kit.quantidade === 'number' && !isNaN(kit.quantidade) ? kit.quantidade : 1;
        
        propostaData[`kit${kitNum}_potencia`] = potencia;
        propostaData[`kit${kitNum}_preco`] = preco;
        propostaData[`kit${kitNum}_quantidade`] = quantidade;
        
        // Garantir que os insumos sejam serializáveis para JSONB
        try {
          if (kit.insumos) {
            const insumosSerializados = JSON.parse(JSON.stringify(kit.insumos));
            propostaData[`kit${kitNum}_insumos`] = insumosSerializados;
          } else {
            propostaData[`kit${kitNum}_insumos`] = null;
          }
        } catch (error) {
          console.error(`❌ Erro ao serializar insumos do kit ${kitNum}:`, error);
          propostaData[`kit${kitNum}_insumos`] = null;
        }
        
        // Extrair informações dos painéis dos insumos para as colunas GENERATED
        // Se for kit manual, usar dados do manualData
        if (kit.tipo === 'manual' && kit.manualData) {
          console.log(`🔍 [removeKit] Buscando módulo para kit manual ${kitNum}:`, {
            modeloPlacaId: kit.manualData?.modeloPlaca,
            totalModulos: modulosAtuais.length
          });
          
          const placaSelecionada = modulosAtuais.find(m => m.id === kit.manualData?.modeloPlaca);
          if (placaSelecionada) {
            // Adicionar descrição do módulo para facilitar busca na página Propostas
            propostaData[`kit${kitNum}_modulo_descricao`] = `${placaSelecionada.marca} ${placaSelecionada.modelo}`;

            // Salvar potência em kW para consistência e para os cálculos da proposta
            propostaData[`kit${kitNum}_potencia_painel`] = placaSelecionada.potencia / 1000;
            propostaData[`kit${kitNum}_quantidade_paineis`] = kit.manualData.quantidadePlacas;
            console.log(`✅ [removeKit] Kit manual ${kitNum}: potência painel=${placaSelecionada.potencia / 1000}kW, quantidade=${kit.manualData.quantidadePlacas}`);
          } else {
            console.error(`❌ [removeKit] Kit manual ${kitNum}: módulo não encontrado!`, {
              modeloPlacaId: kit.manualData?.modeloPlaca,
              modulosIds: modulosAtuais.map(m => m.id)
            });
            propostaData[`kit${kitNum}_potencia_painel`] = 0;
            propostaData[`kit${kitNum}_quantidade_paineis`] = 0;
          }
        } else if (kitTemInsumos(kit)) {
          // Para kits Aldo, usar lógica existente
          let insumosData: InsumoData[] = [];
          
          if (kit.insumos && kit.insumos.length > 0) {
            const firstInsumo = kit.insumos[0];
            if (firstInsumo.Insumos && Array.isArray(firstInsumo.Insumos)) {
              insumosData = firstInsumo.Insumos;
            } else if (firstInsumo.Data) {
              insumosData = Array.isArray(firstInsumo.Data) ? firstInsumo.Data : [firstInsumo.Data];
            }
          }
          
          const painel = insumosData.find((input: InsumoData) => 
            input && (input.Tipo?.toLowerCase().includes('painel') || input.tipo?.toLowerCase().includes('painel'))
          );
          
          if (painel) {
            // Adicionar descrição do módulo para facilitar busca na página Propostas
            const descricaoPainel = painel.Descricao || painel.descricao || '';
            if (descricaoPainel) {
              propostaData[`kit${kitNum}_modulo_descricao`] = descricaoPainel;
            }

            const potenciaPainel = parseFloat((painel.Potencia || painel.potencia || '0').toString()) || 0;
            // Salvar potência em kW para consistência
            propostaData[`kit${kitNum}_potencia_painel`] = potenciaPainel / 1000;
            
            if (potenciaPainel > 0 && !isNaN(kit.potencia)) {
              const quantidadePaineis = Math.round((kit.potencia * 1000) / potenciaPainel) || 0;
              propostaData[`kit${kitNum}_quantidade_paineis`] = quantidadePaineis;
            } else {
              propostaData[`kit${kitNum}_quantidade_paineis`] = 0;
            }
          } else {
            propostaData[`kit${kitNum}_potencia_painel`] = 0;
            propostaData[`kit${kitNum}_quantidade_paineis`] = 0;
          }
        } else {
          propostaData[`kit${kitNum}_potencia_painel`] = 0;
          propostaData[`kit${kitNum}_quantidade_paineis`] = 0;
        }
        
        // Salvar dados do kit manual nos insumos se for kit manual
        if (kit.tipo === 'manual' && kit.manualData) {
          propostaData[`kit${kitNum}_insumos`] = buildManualKitInsumosJson(
            kit,
            modulosAtuais,
            inversoresAtuais,
            bateriasAtuais,
            isLeadSolarHibrido
          );
        }
      });
      
      console.log('📤 Dados da proposta após remoção:', propostaData);
      
      // Verificar se já existe uma proposta para este lead e dimensionamento
      const { data: existingProposta, error: existingError } = await supabase
        .from('propostas_clientes')
        .select('id')
        .eq('lead_id', selectedLead.id)
        .eq('dimensionamento_id', dimensionamentoId)
        .single();

      let result;
      
      if (existingProposta && !existingError) {
        // Atualizar proposta existente
        console.log('🔄 Atualizando proposta existente após remoção do kit...');
        result = await supabase
          .from('propostas_clientes')
          .update(propostaData)
          .eq('id', existingProposta.id);
      } else {
        // Se não há proposta existente e não há kits restantes, não criar uma nova
        if (updatedKits.length === 0) {
          console.log('ℹ️ Nenhum kit restante, não criando proposta vazia');
          toast({
            title: 'Kit removido',
            description: 'Kit removido da seleção. Nenhuma proposta salva (sem kits restantes)'
          });
          return;
        }
        
        // Criar nova proposta apenas se há kits restantes
        console.log('🔄 Criando nova proposta após remoção do kit...');
        result = await supabase
          .from('propostas_clientes')
          .insert(propostaData);
      }
      
      if (result.error) {
        console.error('❌ Erro ao salvar proposta após remoção:', result.error);
        throw new Error(`Erro do Supabase: ${result.error.message}`);
      }
      
      console.log('✅ Kit removido e proposta atualizada com sucesso!');
      
      toast({
        title: 'Kit removido',
        description: `Kit "${kitToRemove.nome}" removido da seleção e salvo no banco de dados`
      });
      
    } catch (error) {
      console.error('❌ Erro ao remover kit:', error);
      
      // Reverter a alteração no estado local em caso de erro
      setSelectedKits(selectedKits);
      
      toast({
        title: 'Erro',
        description: 'Falha ao remover kit: ' + (error as Error).message,
        variant: 'destructive'
      });
    }
  };

  // Função para atualizar quantidade de um kit
  const updateKitQuantity = (index: number, newQuantity: number) => {
    if (newQuantity <= 0) return;
    
    const updatedKits = [...selectedKits];
    updatedKits[index].quantidade = newQuantity;
    setSelectedKits(updatedKits);
    
    // Reativar botão de salvar quando há mudanças
    setIsDataSaved(false);
    setHasUnsavedChanges(true);
  };

  // Função para calcular o total de painéis de um kit
  const calculateTotalPaineis = (kit: SelectedKit): number => {
    console.log('🔍 Calculando total de painéis para kit:', kit.codigo);
    console.log('📦 Kit insumos:', kit.insumos);
    
    if (!kit.insumos) {
      console.log('❌ Kit não tem insumos carregados');
      return 0;
    }
    
    // Verificar se tem a estrutura Insumos (array) ou Data (objeto único)
    let insumos: InsumoData[] = [];
    
    // Verificar se kit.insumos é um array
    if (Array.isArray(kit.insumos)) {
      if (kit.insumos.length === 0) {
        console.log('❌ Kit insumos é um array vazio');
        return 0;
      }
      
      const firstInsumo = kit.insumos[0];
      if (firstInsumo.Insumos && Array.isArray(firstInsumo.Insumos)) {
        insumos = firstInsumo.Insumos;
        console.log('📋 Usando estrutura Insumos (array):', insumos.length);
      } else if (firstInsumo.Data) {
        // Se Data for um array, usar diretamente; se for objeto único, converter para array
        if (Array.isArray(firstInsumo.Data)) {
          insumos = firstInsumo.Data;
          console.log('📋 Usando estrutura Data (array):', insumos.length);
        } else {
          insumos = [firstInsumo.Data];
          console.log('📋 Usando estrutura Data (objeto único) convertido para array');
        }
      } else {
        // Se não tem Insumos nem Data, verificar se o próprio objeto tem propriedades de insumo
        if ((firstInsumo as any).Tipo || (firstInsumo as any).tipo || (firstInsumo as any).Potencia || (firstInsumo as any).potencia) {
          insumos = [firstInsumo as any];
          console.log('📋 Usando estrutura direta (objeto único com propriedades de insumo)');
        } else {
          console.log('❌ Kit não tem estrutura Insumos, Data ou propriedades de insumo válidas');
          console.log('🔍 Estrutura dos insumos:', Object.keys(firstInsumo));
          return 0;
        }
      }
    } else {
      // kit.insumos é um objeto único (estrutura correta do Supabase)
      console.log('🔍 Kit insumos é um objeto único:', kit.insumos);
      
      if ((kit.insumos as any).Insumos && Array.isArray((kit.insumos as any).Insumos)) {
        insumos = (kit.insumos as any).Insumos;
        console.log('📋 Usando estrutura Insumos (array) do objeto:', insumos.length);
      } else if ((kit.insumos as any).Data) {
        // Se Data for um array, usar diretamente; se for objeto único, converter para array
        if (Array.isArray((kit.insumos as any).Data)) {
          insumos = (kit.insumos as any).Data;
          console.log('📋 Usando estrutura Data (array) do objeto:', insumos.length);
        } else {
          insumos = [(kit.insumos as any).Data];
          console.log('📋 Usando estrutura Data (objeto único) do objeto convertido para array');
        }
      } else {
        // Se não tem Insumos nem Data, verificar se o próprio objeto tem propriedades de insumo
        if ((kit.insumos as any).Tipo || (kit.insumos as any).tipo || (kit.insumos as any).Potencia || (kit.insumos as any).potencia) {
          insumos = [kit.insumos as any];
          console.log('📋 Usando estrutura direta (objeto único com propriedades de insumo)');
        } else {
          console.log('❌ Kit não tem estrutura Insumos, Data ou propriedades de insumo válidas no objeto');
          console.log('🔍 Estrutura dos insumos:', Object.keys(kit.insumos));
          return 0;
        }
      }
    }
    
    console.log('📋 Insumos encontrados:', insumos.length);
    
    let totalPaineis = 0;
    
    insumos.forEach((insumo: InsumoData, index: number) => {
      console.log(`🔍 Insumo ${index}:`, insumo);
      
      const tipo = insumo.Tipo || insumo.tipo || '';
      const quantidade = insumo.Quantidade || insumo.quantidade || 0;
      
      console.log(`🔍 Tipo: ${tipo}, Quantidade: ${quantidade}`);
      
      if (tipo.toLowerCase().includes('painel')) {
        console.log(`✅ Painel encontrado: ${tipo} com quantidade ${quantidade}`);
        totalPaineis += quantidade;
      }
    });
    
    const resultado = totalPaineis * kit.quantidade;
    console.log(`📊 Total de painéis para kit ${kit.codigo}: ${resultado}`);
    
    return resultado;
  };

  // Função para calcular o total de painéis de todos os kits
  const calculateTotalPaineisAllKits = (): number => {
    return selectedKits.reduce((total, kit) => {
      // Se for kit manual, usar dados do manualData
      if (kit.tipo === 'manual' && kit.manualData) {
        return total + kit.manualData.quantidadePlacas;
      }
      // Caso contrário, usar função existente para kits Aldo
      return total + calculateTotalPaineis(kit);
    }, 0);
  };

  // Função para calcular a potência total dos inversores de um kit específico
  const calculateTotalPotenciaInversores = (kit: SelectedKit): number => {
    console.log('🔍 Calculando potência dos inversores para kit:', kit.codigo);
    console.log('📦 Kit insumos:', kit.insumos);
    
    if (!kit.insumos) {
      console.log('❌ Kit não tem insumos carregados');
      return 0;
    }
    
    // Verificar se tem a estrutura Insumos (array) ou Data (objeto único)
    let insumos: InsumoData[] = [];
    
    // Verificar se kit.insumos é um array
    if (Array.isArray(kit.insumos)) {
      if (kit.insumos.length === 0) {
        console.log('❌ Kit insumos é um array vazio');
        return 0;
      }
      
      const firstInsumo = kit.insumos[0];
      if (firstInsumo.Insumos && Array.isArray(firstInsumo.Insumos)) {
        insumos = firstInsumo.Insumos;
        console.log('📋 Usando estrutura Insumos (array):', insumos.length);
      } else if (firstInsumo.Data) {
        // Se Data for um array, usar diretamente; se for objeto único, converter para array
        if (Array.isArray(firstInsumo.Data)) {
          insumos = firstInsumo.Data;
          console.log('📋 Usando estrutura Data (array):', insumos.length);
        } else {
          insumos = [firstInsumo.Data];
          console.log('📋 Usando estrutura Data (objeto único) convertido para array');
        }
      } else {
        // Se não tem Insumos nem Data, verificar se o próprio objeto tem propriedades de insumo
        if ((firstInsumo as any).Tipo || (firstInsumo as any).tipo || (firstInsumo as any).Potencia || (firstInsumo as any).potencia) {
          insumos = [firstInsumo as any];
          console.log('📋 Usando estrutura direta (objeto único com propriedades de insumo)');
        } else {
          console.log('❌ Kit não tem estrutura Insumos, Data ou propriedades de insumo válidas');
          console.log('🔍 Estrutura dos insumos:', Object.keys(firstInsumo));
          return 0;
        }
      }
    } else {
      // kit.insumos é um objeto único (estrutura correta do Supabase)
      console.log('🔍 Kit insumos é um objeto único:', kit.insumos);
      
      if ((kit.insumos as any).Insumos && Array.isArray((kit.insumos as any).Insumos)) {
        insumos = (kit.insumos as any).Insumos;
        console.log('📋 Usando estrutura Insumos (array) do objeto:', insumos.length);
      } else if ((kit.insumos as any).Data) {
        // Se Data for um array, usar diretamente; se for objeto único, converter para array
        if (Array.isArray((kit.insumos as any).Data)) {
          insumos = (kit.insumos as any).Data;
          console.log('📋 Usando estrutura Data (array) do objeto:', insumos.length);
        } else {
          insumos = [(kit.insumos as any).Data];
          console.log('📋 Usando estrutura Data (objeto único) do objeto convertido para array');
        }
      } else {
        // Se não tem Insumos nem Data, verificar se o próprio objeto tem propriedades de insumo
        if ((kit.insumos as any).Tipo || (kit.insumos as any).tipo || (kit.insumos as any).Potencia || (kit.insumos as any).potencia) {
          insumos = [kit.insumos as any];
          console.log('📋 Usando estrutura direta (objeto único com propriedades de insumo)');
        } else {
          console.log('❌ Kit não tem estrutura Insumos, Data ou propriedades de insumo válidas no objeto');
          console.log('🔍 Estrutura dos insumos:', Object.keys(kit.insumos));
          return 0;
        }
      }
    }
    
    console.log('📋 Insumos encontrados:', insumos.length);
    
    let totalPotenciaInversores = 0;
    
    insumos.forEach((insumo: InsumoData, index: number) => {
      console.log(`🔍 Insumo ${index}:`, insumo);
      
      const tipo = insumo.Tipo || insumo.tipo || '';
      let potencia = insumo.Potencia || insumo.potencia || 0;
      
      console.log(`🔍 Tipo: ${tipo}, Potência inicial: ${potencia}`);
      
      if (tipo.toLowerCase().includes('inversor') || tipo.toLowerCase().includes('microinversor')) {
        // Se a potência for 0, tentar extrair do nome ou outras propriedades
        if (potencia === 0) {
          // Tentar extrair potência do nome do produto
          const nome = (insumo as any).Descricao || (insumo as any).descricao || (insumo as any).Nome || (insumo as any).nome || '';
          console.log(`🔍 Nome do inversor: ${nome}`);
          
          // Buscar padrões de potência no nome (ex: 3.3KW, 2KW, 3300W, etc.)
          const potenciaMatch = nome.match(/(\d+(?:[.,]\d+)?)\s*(?:KW|kW|kw|W|w)/i);
          if (potenciaMatch) {
            potencia = parseFloat(potenciaMatch[1].replace(',', '.'));
            console.log(`✅ Potência extraída do nome: ${potencia} kW`);
          } else {
            // Tentar outras propriedades possíveis
            potencia = insumo.PotenciaKW || insumo.potenciaKW || insumo.PotenciaW || insumo.potenciaW || 0;
            if (potencia > 0) {
              // Se estiver em watts, converter para kW
              if (potencia > 1000) {
                potencia = potencia / 1000;
              }
              console.log(`✅ Potência encontrada em outras propriedades: ${potencia} kW`);
            } else {
              console.log(`❌ Não foi possível extrair potência do inversor: ${nome}`);
            }
          }
        }
        
        console.log(`✅ Inversor encontrado: ${tipo} com potência ${potencia} kW`);
        totalPotenciaInversores += potencia;
      }
    });
    
    const resultado = totalPotenciaInversores * kit.quantidade;
    console.log(`📊 Potência total dos inversores para kit ${kit.codigo}: ${resultado} kW`);
    
    return resultado;
  };

  // Função para calcular a potência total dos inversores de todos os kits
  const calculateTotalPotenciaInversoresAllKits = (): number => {
    return selectedKits.reduce((total, kit) => {
      // Se for kit manual, calcular potência dos inversores
      if (kit.tipo === 'manual' && kit.manualData) {
        const inversorSelecionado = inversores.find(i => i.id === kit.manualData?.modeloInversor);
        if (inversorSelecionado) {
          const potenciaTotal = (inversorSelecionado.potenciaNominal * kit.manualData.quantidadeInversores) / 1000; // Converter W para kW
          return total + potenciaTotal;
        }
        return total;
      }
      // Caso contrário, usar função existente para kits Aldo
      return total + calculateTotalPotenciaInversores(kit);
    }, 0);
  };

  // Função para limpar apenas os dados da etapa 4 (precificação) e dados das propostas
  const clearEtapa4 = async (leadId: string) => {
    try {
      console.log('🧹 Iniciando limpeza dos dados da etapa 4 (precificação) e dados das propostas para lead:', leadId, 'dimensionamento:', dimensionamentoId);
      
      // PASSO 1: Resetar status de revisão se houver
      if (dimensionamentoId) {
        try {
          console.log('🔄 Verificando e resetando status de revisão...');
          const resetResult = await resetRevisaoOnChanges(
            leadId,
            dimensionamentoId,
            'Etapa 3 (Escolha de Kits)',
            'alteracoes_kits'
          );
          
          if (resetResult.success) {
            // Atualizar estado local para o botão do parceiro refletir imediatamente
            setStatusRevisao('nao_enviada');

            if (resetResult.action === 'reset') {
              console.log('✅ Status de revisão resetado com sucesso');
              toast({
                title: "Revisão Cancelada",
                description: "Devido às alterações nos kits, a revisão em andamento foi cancelada. Será necessário enviar novamente para revisão após refazer a precificação.",
                variant: "default"
              });
            } else {
              console.log('ℹ️ Nenhuma revisão ativa para resetar');
            }
          }
        } catch (resetError) {
          console.error('⚠️ Erro ao resetar status de revisão:', resetError);
          // Continuar com a limpeza mesmo se houver erro no reset
        }
      }
      
      // PASSO 2: Limpar dados da etapa 4 (projecao_grupob)
      const { error: projecaoError } = await supabase
        .from('projecao_grupob')
        .delete()
        .eq('lead_id', leadId)
        .eq('dimensionamento_id', dimensionamentoId);
      
      if (projecaoError) {
        console.warn('⚠️ Erro ao limpar projecao_grupob:', projecaoError);
      } else {
        console.log('✅ Tabela projecao_grupob (etapa 4) limpa com sucesso');
      }
      
      // PASSO 3: Limpar dados de precificação da tabela propostas_clientes
      const { error: precificacaoError } = await supabase
        .from('propostas_clientes')
        .update({
          valor_estrutura_metallica: null,
          valor_fundacao: null,
          valor_equipamentos_alugados: null,
          custo_materiais: null,
          comissao_vendedor: null,
          comissao_parceiro: null,
          comissao_vendedor_valor: null,
          comissao_parceiro_valor: null,
          integracao_r_wp: null,
          impostos_percentual: null,
          mao_obra_por_placa: null,
          preco_projeto: null,
          preco_integracao: null,
          preco_estrutura_metallica_calc: null,
          impostos: null,
          mao_obra: null,
          custo_total_implantacao: null,
          custo_total_integracao: null,
          preco_por_wp_final: null,
          lucro_liquido: null,
          lucro_liquido_percentual: null
        })
        .eq('lead_id', leadId)
        .eq('dimensionamento_id', dimensionamentoId);
      
      if (precificacaoError) {
        console.warn('⚠️ Erro ao limpar dados de precificação em propostas_clientes:', precificacaoError);
      } else {
        console.log('✅ Dados de precificação em propostas_clientes limpos com sucesso');
      }
      
      // PASSO 4: Limpar dados das propostas (análise comparativa e PDF)
      const { error: propostasError } = await supabase
        .from('propostas_clientes')
        .update({
          com_sfv: null,
          proposta_pdf_url: null
        })
        .eq('lead_id', leadId)
        .eq('dimensionamento_id', dimensionamentoId);
      
      if (propostasError) {
        console.warn('⚠️ Erro ao limpar dados das propostas:', propostasError);
      } else {
        console.log('✅ Dados das propostas (com_sfv e proposta_pdf_url) limpos com sucesso');
      }
      
      // Notificar a página principal que a etapa 4 foi limpa
      if (onKitsChanged) {
        onKitsChanged();
      }
      
      console.log('🎯 Limpeza da etapa 4, dados das propostas e status de revisão concluída');
      
    } catch (error) {
      console.error('❌ Erro ao limpar dados da etapa 4 e propostas:', error);
    }
  };

  // Função para salvar proposta no Supabase
  const saveProposta = async () => {
    // Verificação de dados antes de salvar
    if (!selectedLead?.id) {
      toast({
        title: 'Erro',
        description: 'Nenhum lead selecionado',
        variant: 'destructive'
      });
      return;
    }

    if (selectedKits.length === 0) {
      toast({
        title: 'Erro',
        description: 'Nenhum kit selecionado para salvar',
        variant: 'destructive'
      });
      return;
    }

    // Verificar se todos os kits têm insumos carregados (apenas para kits Aldo)
    const kitsAldoSemInsumos = selectedKits.filter(kit => kit.tipo !== 'manual' && !kitTemInsumos(kit));
    if (kitsAldoSemInsumos.length > 0) {
      console.error('❌ Kits Aldo sem insumos:', kitsAldoSemInsumos.map(k => ({ codigo: k.codigo, nome: k.nome })));
      
      // Tentar carregar insumos automaticamente para kits Aldo que não os têm
      console.log('🔄 Tentando carregar insumos automaticamente para kits Aldo sem insumos...');
      for (const kit of kitsAldoSemInsumos) {
        try {
          console.log(`🔄 Carregando insumos para kit ${kit.codigo}...`);
          await fetchKitInsumos(kit);
          console.log(`✅ Insumos carregados para kit ${kit.codigo}`);
        } catch (error) {
          console.error(`❌ Falha ao carregar insumos para kit ${kit.codigo}:`, error);
        }
      }
      
      // Verificar novamente após tentar carregar
      const kitsAldoSemInsumosApos = selectedKits.filter(kit => kit.tipo !== 'manual' && !kitTemInsumos(kit));
      if (kitsAldoSemInsumosApos.length > 0) {
        console.error('❌ Ainda há kits Aldo sem insumos após tentativa de carregamento:', kitsAldoSemInsumosApos.map(k => ({ codigo: k.codigo, nome: k.nome })));
        
        toast({
          title: 'Erro',
          description: `Carregue os insumos de todos os kits antes de salvar. Kits sem insumos: ${kitsSemInsumosApos.map(k => k.codigo).join(', ')}`,
          variant: 'destructive'
        });
        return;
      } else {
        console.log('✅ Todos os kits agora têm insumos carregados');
      }
    }
    
    // Garantir que módulos e inversores estejam carregados antes de salvar
    // Usar variáveis locais para garantir que temos os dados mais recentes
    // IMPORTANTE: Declarar no início da função para estar disponível em todo o código
    let modulosAtuais = modulos;
    let inversoresAtuais = inversores;
    let bateriasAtuais = baterias;
    
    if (modulosAtuais.length === 0 || inversoresAtuais.length === 0) {
      console.log('🔄 Módulos ou inversores não carregados, recarregando...');
      try {
        const modulosData = await modulosService.getAll();
        const inversoresData = await inversoresService.getAll();
        const bateriasData = await bateriasService.getAll();
        modulosAtuais = modulosData;
        inversoresAtuais = inversoresData;
        bateriasAtuais = bateriasData;
        setModulos(modulosData);
        setInversores(inversoresData);
        setBaterias(bateriasData);
        console.log(`✅ Catálogos recarregados: ${modulosData.length} módulos, ${inversoresData.length} inversores, ${bateriasData.length} baterias`);
      } catch (error) {
        console.error('❌ Erro ao recarregar catálogos:', error);
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar os módulos e inversores. Tente novamente.',
          variant: 'destructive'
        });
        return;
      }
    }
    
    // Log para debug dos insumos
    console.log('🔍 Verificando insumos dos kits...');
    selectedKits.forEach((kit, index) => {
      const kitInfo = {
        codigo: kit.codigo,
        nome: kit.nome,
        temInsumos: !!kit.insumos,
        temData: !!(kit.insumos && kit.insumos.Data),
        estrutura: kit.insumos ? Object.keys(kit.insumos) : 'sem insumos',
        potencia: kit.potencia,
        preco: kit.preco,
        quantidade: kit.quantidade
      };
      
      console.log(`🔍 Kit ${index + 1}:`, kitInfo);
      
      // Verificar se os dados do kit são válidos
      if (typeof kit.potencia === 'number' && isNaN(kit.potencia)) {
        console.error(`❌ Kit ${index + 1} tem potência NaN:`, kit.potencia);
      }
      if (typeof kit.preco === 'number' && isNaN(kit.preco)) {
        console.error(`❌ Kit ${index + 1} tem preço NaN:`, kit.preco);
      }
      if (typeof kit.quantidade === 'number' && isNaN(kit.quantidade)) {
        console.error(`❌ Kit ${index + 1} tem quantidade NaN:`, kit.quantidade);
      }
      
      // Verificar se os insumos têm a estrutura esperada
      if (kitTemInsumos(kit)) {
        let insumosData: InsumoData[] = [];
        
        if (kit.insumos && kit.insumos.length > 0) {
          const firstInsumo = kit.insumos[0];
          if (firstInsumo.Insumos && Array.isArray(firstInsumo.Insumos)) {
            insumosData = firstInsumo.Insumos;
          console.log(`📋 Kit ${index + 1} tem ${insumosData.length} insumos (estrutura Insumos)`);
          } else if (firstInsumo.Data) {
            insumosData = Array.isArray(firstInsumo.Data) ? firstInsumo.Data : [firstInsumo.Data];
          console.log(`📋 Kit ${index + 1} tem ${insumosData.length} insumos (estrutura Data)`);
          }
        }
        
        if (insumosData.length > 0) {
          insumosData.forEach((insumo: InsumoData, insumoIndex: number) => {
            if (insumo) {
              console.log(`  - Insumo ${insumoIndex}:`, {
                tipo: insumo.Tipo || insumo.tipo,
                potencia: insumo.Potencia || insumo.potencia,
                codigo: insumo.Codigo || insumo.codigo
              });
            }
          });
        }
      }
    });
    
    console.log('✅ Verificação de insumos concluída');
    
    // Verificar se há algum problema com os dados dos kits
    const kitsComProblemas = selectedKits.filter(kit => {
      return typeof kit.potencia === 'number' && isNaN(kit.potencia) ||
             typeof kit.preco === 'number' && isNaN(kit.preco) ||
             typeof kit.quantidade === 'number' && isNaN(kit.quantidade);
    });
    
    if (kitsComProblemas.length > 0) {
      console.error('❌ Kits com dados inválidos encontrados:', kitsComProblemas.map(k => ({ codigo: k.codigo, nome: k.nome })));
      throw new Error('Kits com dados inválidos (NaN) encontrados. Verifique o console para detalhes.');
    }
    
    console.log('✅ Todos os kits têm dados válidos');

    setLoading(true);
    
    try {
      // Verificar se há mudanças nos kits e limpar dados da etapa 4 se necessário
      if (hasUnsavedChanges) {
        console.log('⚠️ Mudanças detectadas na etapa 3 - limpando dados da etapa 4 antes de salvar');
        await clearEtapa4(selectedLead.id);
        
        // Mostrar aviso ao usuário
        toast({
          title: "Dados das etapas seguintes limpos",
          description: "Como houve alterações na escolha dos kits, os dados de precificação (etapa 4) e das propostas foram removidos para manter a consistência.",
          variant: "default"
        });
      }
      
      // Preparar dados para salvar com validação
      console.log('🔧 Preparando dados da proposta...');
      
      const valorTotalKitsBruto = selectedKits.reduce((total, kit) => {
        const preco = typeof kit.preco === 'number' && !isNaN(kit.preco) ? kit.preco : 0;
        const quantidade = typeof kit.quantidade === 'number' && !isNaN(kit.quantidade) ? kit.quantidade : 1;
        const subtotal = preco * quantidade;
        if (isNaN(subtotal)) {
          console.error(`❌ Subtotal NaN para kit ${kit.codigo}: preco=${preco}, quantidade=${quantidade}`);
          return total;
        }
        return total + subtotal;
      }, 0);
      
      // Aplicar desconto
      const valorTotalKits = Math.max(0, valorTotalKitsBruto - descontoKits);
      console.log(`💰 Valor bruto: R$ ${valorTotalKitsBruto}, Desconto: R$ ${descontoKits}, Valor final: R$ ${valorTotalKits}`);
      
      const potenciaTotalInversores = calculateTotalPotenciaInversoresAllKits() || 0;
      const totalPaineis = calculateTotalPaineisAllKits() || 0;
      
      console.log('💰 Valores calculados:', {
        valorTotalKits,
        potenciaTotalInversores,
        totalPaineis
      });
      
      // Verificar se os valores calculados são válidos
      if (isNaN(valorTotalKits) || isNaN(potenciaTotalInversores) || isNaN(totalPaineis)) {
        console.error('❌ Valores calculados contêm NaN:', {
          valorTotalKits,
          potenciaTotalInversores,
          totalPaineis
        });
        throw new Error('Erro no cálculo dos valores da proposta');
      }
      
      // Verificar se os valores são finitos
      if (!isFinite(valorTotalKits) || !isFinite(potenciaTotalInversores) || !isFinite(totalPaineis)) {
        console.error('❌ Valores calculados contêm infinitos:', {
          valorTotalKits,
          potenciaTotalInversores,
          totalPaineis
        });
        throw new Error('Erro no cálculo dos valores da proposta (valores infinitos)');
      }
      
      // Declarar propostaData aqui, após todas as validações
      // Calcular potência total dos painéis (soma de todos os painéis dos kits)
      const potenciaTotalPaineis = selectedKits.reduce((total, kit) => {
        // Se for kit manual, usar dados do manualData
        if (kit.tipo === 'manual' && kit.manualData) {
          const placaSelecionada = modulosAtuais.find(m => m.id === kit.manualData?.modeloPlaca);
          if (placaSelecionada) {
            return total + (placaSelecionada.potencia * kit.manualData.quantidadePlacas);
          }
          return total;
        }
        
        // Para kits Aldo, usar lógica existente
        if (kitTemInsumos(kit) && kit.insumos && kit.insumos.length > 0) {
          const firstInsumo = kit.insumos[0];
          if (firstInsumo.Insumos && Array.isArray(firstInsumo.Insumos)) {
            const painel = firstInsumo.Insumos.find(insumo => 
              insumo.Tipo && insumo.Tipo.toLowerCase().includes('painel')
            );
            if (painel) {
              const potenciaPainel = parseFloat((painel.Potencia || painel.potencia || '0').toString()) || 0;
              const quantidadePaineis = Math.round((kit.potencia * 1000) / potenciaPainel) || 0;
              return total + (potenciaPainel * quantidadePaineis);
            }
          }
        }
        return total;
      }, 0);

      const propostaData = {
        lead_id: selectedLead.id,
        dimensionamento_id: dimensionamentoId,
        total_kits: selectedKits.length,
        valor_total_kits: valorTotalKits,
        desconto_kits: descontoKits,
        potencia_total_inversores: potenciaTotalInversores,
        // potencia_total_paineis é uma coluna GENERATED - calculada automaticamente pelo PostgreSQL
        total_paineis: totalPaineis,
        created_by: user?.id || null // CORREÇÃO: Adicionar created_by para rastrear quem criou/editou
      };

      // Adicionar dados dos kits com verificação
      console.log('🔧 Adicionando dados dos kits...');
      selectedKits.forEach((kit, index) => {
        const kitNum = index + 1;
        console.log(`🔧 Processando kit ${kitNum}: ${kit.codigo} - ${kit.nome}`);
        
        // Garantir que o código seja sempre uma string (suporta kits Aldo numéricos e kits manuais com strings)
        propostaData[`kit${kitNum}_codigo`] = kit.codigo ? String(kit.codigo) : null;
        propostaData[`kit${kitNum}_nome`] = kit.nome || null;
        
        // Validar e definir dados do kit com proteção contra NaN
        const potencia = typeof kit.potencia === 'number' && !isNaN(kit.potencia) ? kit.potencia : 0;
        const preco = typeof kit.preco === 'number' && !isNaN(kit.preco) ? kit.preco : 0;
        const quantidade = typeof kit.quantidade === 'number' && !isNaN(kit.quantidade) ? kit.quantidade : 1;
        
        propostaData[`kit${kitNum}_potencia`] = potencia;
        propostaData[`kit${kitNum}_preco`] = preco;
        propostaData[`kit${kitNum}_quantidade`] = quantidade;
        
        console.log(`✅ Kit ${kitNum} dados básicos:`, { potencia, preco, quantidade });
        
        // Garantir que os insumos sejam serializáveis para JSONB
        // Se for kit manual, criar estrutura de insumos
        if (kit.tipo === 'manual' && kit.manualData) {
          const placaSelecionada = modulosAtuais.find(m => m.id === kit.manualData?.modeloPlaca);
          
          // Adicionar descrição do módulo para facilitar busca na página Propostas
          if (placaSelecionada) {
            propostaData[`kit${kitNum}_modulo_descricao`] = `${placaSelecionada.marca} ${placaSelecionada.modelo}`;
          }

          propostaData[`kit${kitNum}_insumos`] = buildManualKitInsumosJson(
            kit,
            modulosAtuais,
            inversoresAtuais,
            bateriasAtuais,
            isLeadSolarHibrido
          );
          console.log(`✅ Insumos do kit manual ${kitNum} criados com sucesso`);
        } else {
          // Para kits Aldo, usar lógica existente
          try {
            if (kit.insumos) {
              const insumosSerializados = JSON.parse(JSON.stringify(kit.insumos));
              propostaData[`kit${kitNum}_insumos`] = insumosSerializados;
              console.log(`✅ Insumos do kit ${kitNum} serializados com sucesso`);
            } else {
              propostaData[`kit${kitNum}_insumos`] = null;
              console.log(`ℹ️ Kit ${kitNum} não tem insumos`);
            }
          } catch (error) {
            console.error(`❌ Erro ao serializar insumos do kit ${kitNum}:`, error);
            propostaData[`kit${kitNum}_insumos`] = null;
          }
        }
        
        // Extrair informações dos painéis dos insumos para as colunas GENERATED
        // Se for kit manual, usar dados do manualData
        if (kit.tipo === 'manual' && kit.manualData) {
          console.log(`🔍 Buscando módulo para kit manual ${kitNum}:`, {
            modeloPlacaId: kit.manualData?.modeloPlaca,
            totalModulos: modulosAtuais.length,
            modulosDisponiveis: modulosAtuais.map(m => ({ id: m.id, marca: m.marca, modelo: m.modelo }))
          });
          
          const placaSelecionada = modulosAtuais.find(m => m.id === kit.manualData?.modeloPlaca);
          
          if (placaSelecionada) {
            // Salvar potência em kW para consistência e para os cálculos da proposta
            propostaData[`kit${kitNum}_potencia_painel`] = placaSelecionada.potencia / 1000;
            propostaData[`kit${kitNum}_quantidade_paineis`] = kit.manualData.quantidadePlacas;
            console.log(`✅ Kit manual ${kitNum}: potência painel=${placaSelecionada.potencia / 1000}kW, quantidade=${kit.manualData.quantidadePlacas}`);
          } else {
            // Tentar buscar a potência dos insumos se o módulo não foi encontrado
            let potenciaDoInsumo = 0;
            if (propostaData[`kit${kitNum}_insumos`]) {
              const insumos = propostaData[`kit${kitNum}_insumos`] as any;
              if (insumos.Insumos && Array.isArray(insumos.Insumos)) {
                const painelInsumo = insumos.Insumos.find((i: any) => i.Tipo === 'PAINEL' || i.Tipo === 'Painel Solar');
                if (painelInsumo && painelInsumo.Potencia) {
                  potenciaDoInsumo = painelInsumo.Potencia;
                }
              }
            }
            
            console.error(`❌ Kit manual ${kitNum}: módulo não encontrado!`, {
              modeloPlacaId: kit.manualData?.modeloPlaca,
              totalModulos: modulosAtuais.length,
              modulosIds: modulosAtuais.map(m => m.id),
              modulosDisponiveis: modulosAtuais.map(m => ({ id: m.id, marca: m.marca, modelo: m.modelo })),
              potenciaDoInsumo
            });
            
            // Usar potência do insumo se disponível (já está em kW nos insumos), senão usar 0
            propostaData[`kit${kitNum}_potencia_painel`] = potenciaDoInsumo; // Já está em kW nos insumos
            propostaData[`kit${kitNum}_quantidade_paineis`] = kit.manualData.quantidadePlacas;
            console.log(`ℹ️ Kit manual ${kitNum}: usando potência do insumo=${potenciaDoInsumo}kW, quantidade=${kit.manualData.quantidadePlacas}`);
          }
        } else if (kitTemInsumos(kit)) {
          let insumosData: InsumoData[] = [];
          
          if (kit.insumos && kit.insumos.length > 0) {
            const firstInsumo = kit.insumos[0];
            if (firstInsumo.Insumos && Array.isArray(firstInsumo.Insumos)) {
              insumosData = firstInsumo.Insumos;
            } else if (firstInsumo.Data) {
              insumosData = Array.isArray(firstInsumo.Data) ? firstInsumo.Data : [firstInsumo.Data];
            }
          }
          
          const painel = insumosData.find((input: InsumoData) => 
            input && (input.Tipo?.toLowerCase().includes('painel') || input.tipo?.toLowerCase().includes('painel'))
          );
          
          if (painel) {
            // Adicionar descrição do módulo para facilitar busca na página Propostas
            const descricaoPainel = painel.Descricao || painel.descricao || '';
            if (descricaoPainel) {
              propostaData[`kit${kitNum}_modulo_descricao`] = descricaoPainel;
            }

            // Potência do painel em W
            const potenciaPainel = parseFloat((painel.Potencia || painel.potencia || '0').toString()) || 0;
            // Salvar potência em kW para consistência
            propostaData[`kit${kitNum}_potencia_painel`] = potenciaPainel / 1000;
            
            // Quantidade de painéis (assumindo que é baseada na potência do kit)
            if (potenciaPainel > 0 && !isNaN(kit.potencia)) {
              const quantidadePaineis = Math.round((kit.potencia * 1000) / potenciaPainel) || 0;
              propostaData[`kit${kitNum}_quantidade_paineis`] = quantidadePaineis;
              console.log(`✅ Kit ${kitNum}: potência painel=${potenciaPainel / 1000}kW, quantidade=${quantidadePaineis}`);
            } else {
              propostaData[`kit${kitNum}_quantidade_paineis`] = 0;
              console.log(`ℹ️ Kit ${kitNum}: potência painel=${potenciaPainel / 1000}kW, quantidade=0 (potência kit inválida: ${kit.potencia})`);
            }
          } else {
            // Se não encontrar painel, definir valores padrão
            propostaData[`kit${kitNum}_potencia_painel`] = 0;
            propostaData[`kit${kitNum}_quantidade_paineis`] = 0;
            console.log(`ℹ️ Kit ${kitNum}: painel não encontrado, valores padrão definidos`);
          }
        } else {
          // Se não houver insumos, definir valores padrão
          propostaData[`kit${kitNum}_potencia_painel`] = 0;
          propostaData[`kit${kitNum}_quantidade_paineis`] = 0;
          console.log(`ℹ️ Kit ${kitNum}: sem insumos, valores padrão definidos`);
        }
      });
      
      console.log('✅ Dados dos kits adicionados com sucesso');
      
      // Verificar se todos os campos dos kits foram preenchidos corretamente
      const camposKits = ['codigo', 'nome', 'potencia', 'preco', 'quantidade', 'insumos', 'potencia_painel', 'quantidade_paineis'];
      for (let i = 1; i <= selectedKits.length; i++) {
        const camposFaltando = camposKits.filter(campo => !propostaData[`kit${i}_${campo}`] && propostaData[`kit${i}_${campo}`] !== 0);
        if (camposFaltando.length > 0) {
          console.warn(`⚠️ Kit ${i} tem campos faltando:`, camposFaltando);
        }
      }

      // Log dos dados que serão enviados para debug
      console.log('📤 Dados da proposta a serem enviados:', JSON.stringify(propostaData, null, 2));
      
      // Verificar se há algum problema com os dados antes de enviar
      console.log('🔍 Validando dados da proposta...');
      const dadosValidos = Object.entries(propostaData).every(([key, value]) => {
        if (value === null || value === undefined) return true; // Valores null são permitidos
        if (typeof value === 'number' && isNaN(value)) {
          console.error(`❌ Valor NaN encontrado na coluna ${key}:`, value);
          return false;
        }
        if (typeof value === 'string' && value.includes('NaN')) {
          console.error(`❌ String com NaN encontrada na coluna ${key}:`, value);
          return false;
        }
        if (typeof value === 'number' && !isFinite(value)) {
          console.error(`❌ Valor infinito encontrado na coluna ${key}:`, value);
          return false;
        }
        return true;
      });
      
      if (!dadosValidos) {
        throw new Error('Dados inválidos detectados (NaN ou infinito). Verifique o console para detalhes.');
      }
      
      console.log('✅ Validação dos dados concluída com sucesso');
      
      // Verificar se todos os campos obrigatórios estão preenchidos
      const camposObrigatorios = ['lead_id', 'total_kits', 'valor_total_kits'];
      const camposFaltando = camposObrigatorios.filter(campo => !propostaData[campo]);
      
      if (camposFaltando.length > 0) {
        console.error('❌ Campos obrigatórios faltando:', camposFaltando);
        throw new Error(`Campos obrigatórios faltando: ${camposFaltando.join(', ')}`);
      }
      
      console.log('✅ Todos os campos obrigatórios estão preenchidos');
      
      // Verificar se há campos com valores inválidos
      const camposComProblemas = Object.entries(propostaData).filter(([key, value]) => {
        if (value === null || value === undefined) return false; // Valores null são permitidos
        if (typeof value === 'number' && (isNaN(value) || !isFinite(value))) return true;
        if (typeof value === 'string' && value.includes('NaN')) return true;
        return false;
      });
      
      if (camposComProblemas.length > 0) {
        console.error('❌ Campos com problemas encontrados:', camposComProblemas);
        throw new Error(`Campos com problemas: ${camposComProblemas.map(([key]) => key).join(', ')}`);
      }
      
      console.log('✅ Todos os campos estão com valores válidos');
      
      // Verificar se há campos com valores muito grandes ou muito pequenos
      const camposComValoresExtremos = Object.entries(propostaData).filter(([key, value]) => {
        if (typeof value === 'number' && value !== 0) {
          if (value > 1e9 || value < -1e9) {
            console.warn(`⚠️ Campo ${key} tem valor extremo:`, value);
            return true;
          }
        }
        return false;
      });
      
      if (camposComValoresExtremos.length > 0) {
        console.warn('⚠️ Campos com valores extremos encontrados:', camposComValoresExtremos);
      }
      
      // Verificar se já existe uma proposta para este lead e dimensionamento
      console.log('🔍 Verificando se já existe proposta para o lead e dimensionamento...');
      const { data: existingProposta, error: existingError } = await supabase
        .from('propostas_clientes')
        .select('id')
        .eq('lead_id', selectedLead.id)
        .eq('dimensionamento_id', dimensionamentoId)
        .single();

      let result;
      
      try {
        if (existingProposta && !existingError) {
          // Atualizar proposta existente
          console.log('🔄 Atualizando proposta existente ID:', existingProposta.id);
          result = await supabase
            .from('propostas_clientes')
            .update(propostaData)
            .eq('id', existingProposta.id);
        } else {
          // Criar nova proposta
          console.log('🔄 Criando nova proposta...');
          result = await supabase
            .from('propostas_clientes')
            .insert(propostaData);
        }
        
        console.log('📡 Resultado da operação:', result);
        
        // Log detalhado do resultado
        if (result.error) {
          console.error('❌ Erro detalhado:', {
            message: result.error.message,
            code: result.error.code,
            details: result.error.details,
            hint: result.error.hint,
            data: result.data
          });
        } else {
          console.log('✅ Operação bem-sucedida:', {
            data: result.data,
            count: result.count
          });
        }
      } catch (dbError) {
        console.error('❌ Erro na operação do banco:', dbError);
        console.error('❌ Tipo de erro:', dbError.constructor.name);
        console.error('❌ Mensagem:', dbError.message);
        throw new Error(`Erro na operação do banco: ${dbError.message}`);
      }

      if (!result) {
        console.error('❌ Resposta inválida do Supabase:', result);
        throw new Error('Resposta inválida do Supabase');
      }
      
      // Para operações de UPDATE, o Supabase retorna status 204 (No Content) sem dados
      // Para operações de INSERT, retorna status 201 (Created) com dados
      if (result.error) {
        console.error('❌ Erro retornado pelo Supabase:', result.error);
        throw new Error(`Erro do Supabase: ${result.error.message} (${result.error.code})`);
      }
      
      // Verificar se a operação foi bem-sucedida baseada no status HTTP
      if (result.status === 204 || result.status === 201 || result.status === 200) {
        console.log('✅ Operação no banco concluída com sucesso');
        
        // Marcar dimensionamento como não finalizado quando há alterações
        try {
          const { PropostasService } = await import('@/services/propostasService');
          await PropostasService.marcarDimensionamentoNaoFinalizado(selectedLead.id, dimensionamentoId);
          console.log('✅ Dimensionamento marcado como não finalizado após alterações na etapa 3');
        } catch (error) {
          console.error('❌ Erro ao marcar dimensionamento como não finalizado:', error);
          // Não falhar o salvamento se houver erro ao marcar como não finalizado
        }
        
        console.log('✅ Kit(s) salvo(s) com sucesso!');
        toast({
          title: 'Sucesso',
          description: 'Kit(s) salvo(s) com sucesso!'
        });
        
        // Atualizar kits originais com os valores atuais
        setOriginalKits(selectedKits.map(kit => ({ ...kit }))); // Cópia profunda
        setOriginalDescontoKits(descontoKits); // Atualizar desconto original
        setIsDataSaved(true);
        setHasUnsavedChanges(false);
        
        console.log('💾 Kits originais e desconto atualizados após salvamento');
        
        // Log final de sucesso
        console.log('🎉 Processo de salvamento concluído com sucesso!');
        
        // Log dos dados salvos para confirmação
        console.log('📊 Dados salvos com sucesso:', {
          lead_id: propostaData.lead_id,
          total_kits: propostaData.total_kits,
          valor_total_kits: propostaData.valor_total_kits,
          potencia_total_inversores: propostaData.potencia_total_inversores,
          total_paineis: propostaData.total_paineis
        });
      } else {
        console.error('❌ Status HTTP inesperado:', result.status);
        throw new Error(`Status HTTP inesperado: ${result.status}`);
      }
    } catch (error) {
      console.error('❌ Erro ao salvar proposta:', error);
      console.error('❌ Stack trace:', (error as Error).stack);
      
      // Log adicional para debug
      if (error instanceof Error) {
        console.error('❌ Tipo de erro:', error.constructor.name);
        console.error('❌ Mensagem:', error.message);
        console.error('❌ Nome:', error.name);
      }
      
      // Log do contexto atual
      console.error('❌ Contexto do erro:', {
        selectedKits: selectedKits.length,
        selectedLead: selectedLead?.id,
        token: token ? 'disponível' : 'não disponível'
      });
      
      toast({
        title: 'Erro',
        description: 'Falha ao salvar proposta: ' + (error as Error).message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar insumos de um kit
  const fetchKitInsumos = async (kit: SelectedKit) => {
    if (!token) {
      toast({ title: 'Erro', description: 'Token não disponível', variant: 'destructive' });
      return;
    }
    
    // Verificar se o token ainda é válido tentando renovar se necessário
    if (token && token.length < 10) {
      console.log('🔄 Token parece inválido, tentando renovar...');
      const tokenRenovado = await regenerateToken();
      if (!tokenRenovado) {
        toast({ title: 'Erro', description: 'Falha ao renovar token', variant: 'destructive' });
        return;
      }
    }
    
    setLoading(true);
    try {
      const response = await fetch('/api/aldo/products-inputs-python', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, codigoProduto: kit.codigo.toString() }),
      });
      
      const data = await response.json();
      
      if (data && !data.error) {
        // Atualizar kit com insumos
        const updatedKits = selectedKits.map(k => 
          k.codigo === kit.codigo ? { ...k, insumos: data } : k
        );
        setSelectedKits(updatedKits);
        
        toast({ title: 'Sucesso', description: 'Insumos carregados com sucesso' });
      } else {
        // Verificar se é erro de token expirado
        if (data.error && data.error.includes('401')) {
          console.log('🔄 Token expirado, tentando renovar...');
          await regenerateToken();
          // Tentar novamente com o novo token
          if (token) {
            const retryResponse = await fetch('/api/aldo/products-inputs-python', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token, codigoProduto: kit.codigo.toString() }),
            });
            
            const retryData = await retryResponse.json();
            
            if (retryData && !retryData.error) {
              // Atualizar kit com insumos
              const updatedKits = selectedKits.map(k => 
                k.codigo === kit.codigo ? { ...k, insumos: retryData } : k
              );
              setSelectedKits(updatedKits);
              
              toast({ title: 'Sucesso', description: 'Insumos carregados com sucesso (token renovado)' });
            } else {
              throw new Error(retryData.error || 'Falha ao carregar insumos após renovar token');
            }
          } else {
            throw new Error('Falha ao renovar token');
          }
        } else {
          throw new Error(data.error || 'Falha ao carregar insumos');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar insumos:', error);
      toast({ 
        title: 'Erro', 
        description: 'Falha ao carregar insumos: ' + (error as Error).message, 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Função para renovar token automaticamente
  const regenerateToken = async () => {
    try {
      console.log('🔄 Renovando token...');
      const tokenResponse = await fetch('/api/aldo/token-python', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      
      const tokenData = await tokenResponse.json();
      
      if (tokenData.Data && tokenData.Data.Token) {
        const newToken = tokenData.Data.Token;
        setToken(newToken);
        console.log('✅ Token renovado com sucesso');
        return true;
      } else {
        console.error('❌ Falha ao renovar token:', tokenData);
        return false;
      }
    } catch (error) {
      console.error('❌ Erro ao renovar token:', error);
      return false;
    }
  };

  const handleShowInputsClose = () => {
    setShowInputs(false);
    setSelectedProduct(null);
    setProductInputs(null);
  };
  
  const generateTokenAndFetchKits = async () => {
    setLoading(true);
    console.log('🔄 Iniciando geração de token e busca de kits...');
    
    try {
      // Primeiro, gerar o token
      console.log('📡 Fazendo requisição para token...');
      const tokenResponse = await fetch('/api/aldo/token-python', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      
      console.log('📡 Resposta do token recebida:', tokenResponse.status, tokenResponse.statusText);
      const tokenData = await tokenResponse.json();
      console.log('📡 Dados do token recebidos:', tokenData);
      
      if (tokenData.Data && tokenData.Data.Token) {
        const newToken = tokenData.Data.Token;
        setToken(newToken);
        console.log('✅ Token definido com sucesso');
        
        // Imediatamente buscar os kits com o token gerado
        await fetchKitsWithToken(newToken);
      } else {
        console.error('❌ Resposta não contém token válido:', tokenData);
        throw new Error('Falha ao gerar token');
      }
    } catch (error) {
      console.error('❌ Erro ao gerar token e buscar kits:', error);
      toast({ 
        title: 'Erro', 
        description: 'Falha ao gerar token e buscar kits: ' + (error as Error).message, 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  const buildFiltroAtributos = () => {
    const filters = [];
    
    // Add selected filters
    Object.entries(selectedFilters).forEach(([filterId, value]) => {
        if (value && value !== 'all') {
            filters.push(`${filterId}:${value}`);
        }
    });
    
    // Add hidden filters with predefined values
    // Bateria: always "Todos" (don't add to filters)
    // Bomba da agua solar: always "Todos" (don't add to filters)
    // Tipo Produto: always "Gerador" (ID 23)
    filters.push(`1:23`);
    
    // Add power filter with maximum value (30000) if only minimum is provided
    if (potenciaMin) {
      filters.push(`3:${potenciaMin}~30000`);
    }
    
    return filters.length > 0 ? filters.join(';') : null;
  };
  
  const fetchKitsWithToken = async (tokenToUse: string) => {
    try {
      const filtroAtributos = buildFiltroAtributos();
      
      const body = {
        slug: 'energia-solar/gerador-de-energia-solar-fotovoltaico',
        origem: 'categoria',
        filtroAtributos: filtroAtributos,
        offset: 0,
        filterId: 0,
        orderby: 1 // Ordenar por menor preço
      };
      
      const response = await fetch('/api/aldo/products-python', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenToUse, body }),
      });
      
      const data = await response.json();
      
      if (data.Produtos) {
        // Sort products by price in ascending order
        const sortedProducts = [...data.Produtos].sort((a, b) => a.Preco - b.Preco);
        setProducts(sortedProducts);
        toast({ 
          title: 'Sucesso', 
          description: `Kits encontrados: ${sortedProducts.length} itens` 
        });
      } else {
        throw new Error(data.error || 'Falha ao carregar kits');
      }
    } catch (error) {
      console.error('Erro ao carregar kits:', error);
      toast({ 
        title: 'Erro', 
        description: 'Falha ao carregar kits: ' + (error as Error).message, 
        variant: 'destructive' 
      });
    }
  };
  
  const fetchProductInputs = async (product: Product) => {
    if (!token) {
      toast({ title: 'Erro', description: 'Token não disponível', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch('/api/aldo/products-inputs-python', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, codigoProduto: product.Codigo.toString() }),
      });
      
      const data = await response.json();
      
      if (data && !data.error) {
        setSelectedProduct(product);
        setProductInputs(data);
        setShowInputs(true);
        toast({ title: 'Sucesso', description: 'Insumos carregados com sucesso' });
      } else {
        throw new Error(data.error || 'Falha ao carregar insumos');
      }
    } catch (error) {
      console.error('Erro ao carregar insumos:', error);
      toast({ 
        title: 'Erro', 
        description: 'Falha ao carregar insumos: ' + (error as Error).message, 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilterChange = (filterId: number, value: string) => {
    setSelectedFilters(prev => {
      if (value === 'all' || value === '') {
        // Remove the filter if "all" is selected or if value is empty
        const { [filterId]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [filterId]: value
      };
    });
  };
  
  return (
    <div className="space-y-6">
      {/* Informações do Dimensionamento */}
      {dimensionamentoData ? (
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-green-800 text-lg flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Resumo do Dimensionamento - Etapa 2
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`grid gap-4 ${
              isTelhadoFixacao(dimensionamentoData.tipoFixacao) 
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5' 
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
            }`}>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                <Zap className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Potência do Sistema</p>
                  <p className="text-2xl font-bold text-green-800">
                    {dimensionamentoData.systemPowerKWp} kWp
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                <TrendingUp className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Geração Anual</p>
                  <p className="text-2xl font-bold text-green-800">
                    {dimensionamentoData.annualGenPerKwp} kWh/kWp/ano
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                <Wrench className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Tipo de Fixação</p>
                  <p className="text-lg font-semibold text-blue-800">
                    {dimensionamentoData.tipoFixacao || 'Não definido'}
                  </p>
                </div>
              </div>

              {isTelhadoFixacao(dimensionamentoData.tipoFixacao) && (
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                  <Home className="h-8 w-8 text-orange-500" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Tipo de Telha</p>
                    <p className="text-lg font-semibold text-orange-800">
                      {dimensionamentoData.tipoTelha || 'Não definido'}
                    </p>
                  </div>
                </div>
              )}

              {(dimensionamentoData.grupoTarifario === 'Grupo A' || dimensionamentoData.grupoTarifario === 'Mercado Livre') && (
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                  <Calculator className="h-8 w-8 text-indigo-500" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Consumo Efetivo Anual</p>
                    <p className="text-2xl font-bold text-indigo-800">
                      {dimensionamentoData.consumoEfetivoAnual || '0'} kWh/ano
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Padrão:</strong> {dimensionamentoData?.padrao || selectedLead?.padrao || 'Não definido'} | 
                <strong> Grupo Tarifário:</strong> {dimensionamentoData?.grupoTarifario || selectedLead?.grupo_tarifario || 'Não definido'} | 
                <strong> Localização:</strong> {selectedLead?.cidade || 'Não definido'}, {selectedLead?.estado || 'Não definido'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-yellow-800 text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Dados do Dimensionamento Não Encontrados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-yellow-700">
              Não foi possível carregar os dados do dimensionamento da Etapa 2. 
              Verifique se o dimensionamento foi concluído e se o lead possui um grupo tarifário válido.
            </p>
            <div className="mt-3 p-3 bg-white rounded-lg border">
              <p className="text-sm text-gray-600">
                <strong>Lead ID:</strong> {selectedLead?.id || 'N/A'}<br/>
                <strong>Grupo Tarifário:</strong> {selectedLead?.grupo_tarifario || 'Não definido'}<br/>
                <strong>Padrão:</strong> {selectedLead?.padrao || 'Não definido'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Kits Selecionados */}
      {selectedKits.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-blue-800 text-lg flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Kits Selecionados ({selectedKits.length}/4)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedKits.map((kit, index) => (
                <div key={kit.codigo} className="bg-white p-4 rounded-lg border">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-sm text-blue-800">Kit {index + 1}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeKitFromSelection(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <p><strong>Nome:</strong> {kit.nome}</p>
                      {kit.tipo === 'manual' && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Manual</span>
                      )}
                      {kit.tipo === 'aldo' && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Aldo</span>
                      )}
                    </div>
                    <p><strong>Potência:</strong> {kit.potencia?.toFixed(2)} kWp</p>
                    <p><strong>Preço:</strong> R$ {formatBrazilianCurrencyValue(kit.preco || 0)}</p>
                    
                    {/* Informações adicionais para kits manuais */}
                    {kit.tipo === 'manual' && kit.manualData && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                        <p><strong>Placa:</strong> {modulos.find(m => m.id === kit.manualData?.modeloPlaca)?.marca} {modulos.find(m => m.id === kit.manualData?.modeloPlaca)?.modelo} ({kit.manualData.quantidadePlacas}x)</p>
                        <p><strong>Inversor:</strong> {inversores.find(i => i.id === kit.manualData?.modeloInversor)?.marca} {inversores.find(i => i.id === kit.manualData?.modeloInversor)?.modelo} ({kit.manualData.quantidadeInversores}x)</p>
                        {kit.manualData.modeloBateria && (
                          <p><strong>Bateria:</strong> {baterias.find((b) => b.id === kit.manualData?.modeloBateria)?.marca} {baterias.find((b) => b.id === kit.manualData?.modeloBateria)?.modelo} ({kit.manualData.quantidadeBaterias ?? 1}x)</p>
                        )}
                        <p><strong>Estrutura:</strong> {kit.manualData.tipoEstrutura}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`quantity-${index}`} className="text-xs">Quantidade:</Label>
                      <Input
                        id={`quantity-${index}`}
                        type="number"
                        min="1"
                        value={kit.quantidade}
                        onChange={(e) => updateKitQuantity(index, parseInt(e.target.value) || 1)}
                        className="w-20 h-8 text-xs"
                      />
                    </div>
                    
                    <p><strong>Subtotal:</strong> R$ {formatBrazilianCurrencyValue((kit.preco || 0) * kit.quantidade)}</p>
                    
                    {kit.tipo !== 'manual' && !kitTemInsumos(kit) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchKitInsumos(kit)}
                        disabled={loading}
                        className="w-full text-red-600 border-red-600 hover:text-red-700 hover:border-red-700"
                      >
                        Carregar Insumos
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
                <div className="space-y-2 flex-1 min-w-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-lg font-semibold text-blue-800">
                        Total Bruto: R$ {formatBrazilianCurrencyValue(selectedKits.reduce((total, kit) => total + ((kit.preco || 0) * kit.quantidade), 0))}
                      </p>
                      <p className="text-lg font-semibold text-blue-800">
                        Potência Total do Sistema: {selectedKits.reduce((total, kit) => total + ((kit.potencia || 0) * kit.quantidade), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWp
                      </p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-green-800">
                        Potência Total dos Inversores: {calculateTotalPotenciaInversoresAllKits().toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kW
                      </p>
                      <p className="text-lg font-semibold text-green-800">
                        Total de Painéis: {calculateTotalPaineisAllKits()} unidades
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {selectedKits.reduce((total, kit) => total + kit.quantidade, 0)} unidades | {selectedKits.length} tipos de kits
                  </p>
                  {!selectedKits.every(kit => kitTemInsumos(kit)) && (
                    <p className="text-sm text-orange-600 font-medium">
                      ⚠️ {selectedKits.filter(kit => !kitTemInsumos(kit)).length} kit(s) sem insumos carregados
                    </p>
                  )}
                  
                  {/* Campo de Desconto */}
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label htmlFor="desconto-kits" className="block text-sm font-medium text-yellow-800 mb-1">
                          Desconto sobre Kits (R$)
                        </label>
                        <input
                          id="desconto-kits"
                          type="text"
                          value={descontoKits === 0 ? '' : formatBrazilianCurrencyValue(descontoKits)}
                          onChange={(e) => {
                            const formatted = formatCurrencyInput(e.target.value);
                            const value = parseBrazilianCurrency(formatted);
                            setDescontoKits(isNaN(value) ? 0 : value);
                            setHasUnsavedChanges(true);
                            setIsDataSaved(false);
                          }}
                          onKeyDown={(e) => {
                            // Permite apenas números e teclas de controle
                            const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                            if (allowedKeys.includes(e.key) || /^\d$/.test(e.key)) {
                              return;
                            }
                            e.preventDefault();
                          }}
                          className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          placeholder="0,00"
                        />
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-yellow-700 mb-1">Valor Final:</p>
                        <p className="text-xl font-bold text-green-600">
                          R$ {formatBrazilianCurrencyValue(Math.max(0, selectedKits.reduce((total, kit) => total + ((kit.preco || 0) * kit.quantidade), 0) - descontoKits))}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={saveProposta} 
                  disabled={readOnly || loading || !selectedKits.every(kit => kitTemInsumos(kit)) || (isDataSaved && !hasUnsavedChanges)} 
                  className="w-full sm:w-auto shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  title={
                    !selectedKits.every(kit => kitTemInsumos(kit)) 
                      ? 'Carregue os insumos de todos os kits antes de salvar' 
                      : (isDataSaved && !hasUnsavedChanges)
                        ? 'Kits já salvos. Selecione um novo kit para habilitar o salvamento'
                        : ''
                  }
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Salvando...' : (isDataSaved && !hasUnsavedChanges) ? 'Kits Salvos' : 'Salvar Kits'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alertas de Status */}
      {selectedKits.length > 0 && (
        <div className="space-y-3">
          {isDataSaved && !hasUnsavedChanges && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <h4 className="text-green-800 font-medium">Kits Confirmados</h4>
              </div>
              <p className="text-green-700 text-sm mt-1">
                {isParceiro
                  ? 'Os kits foram salvos com sucesso. Clique em "Enviar para Precificação" para concluir sua parte do dimensionamento.'
                  : 'Os kits foram salvos com sucesso. Clique em "Próximo" para continuar.'}
              </p>
            </div>
          )}

          {!isDataSaved && selectedKits.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                <h4 className="text-yellow-800 font-medium">Kits Não Salvos</h4>
              </div>
              <p className="text-yellow-700 text-sm mt-1">
                Os kits foram selecionados mas ainda não foram salvos. Clique em "Salvar Kits" antes de prosseguir.
              </p>
            </div>
          )}

          {hasUnsavedChanges && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                <h4 className="text-orange-800 font-medium">Alterações Não Salvas</h4>
              </div>
              <p className="text-orange-700 text-sm mt-1">
                Existem alterações não salvas nos kits. Salve os kits antes de continuar para a próxima etapa.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Seleção de Tipo de Kit */}
      {kitType === null && (
        <Card>
          <CardHeader>
            <CardTitle>Escolha do Tipo de Kit</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={() => setKitType('aldo')}
                className="h-32 flex flex-col items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700"
              >
                <Zap className="h-8 w-8" />
                <span className="text-lg font-semibold">Kit Aldo Solar</span>
                <span className="text-sm opacity-90">Escolher kit da distribuidora Aldo</span>
              </Button>
              <Button
                onClick={() => setKitType('manual')}
                className="h-32 flex flex-col items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <Wrench className="h-8 w-8" />
                <span className="text-lg font-semibold">Kit Manual</span>
                <span className="text-sm opacity-90">Montar kit personalizado</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Formulário de Kit Manual */}
      {kitType === 'manual' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Kit Manual</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setKitType(null);
                  setManualKitData({
                    marcaPlaca: '',
                    modeloPlaca: '',
                    quantidadePlacas: 0,
                    marcaInversor: '',
                    modeloInversor: '',
                    quantidadeInversores: 0,
                    marcaBateria: '',
                    modeloBateria: '',
                    quantidadeBaterias: 0,
                    tipoEstrutura: '',
                    precoTotal: 0
                  });
                  // Resetar estados de edição
                  setEditingQuantidadePlacas('');
                  setEditingQuantidadeInversores('');
                  setEditingQuantidadeBaterias('');
                }}
              >
                Voltar
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Informações das Placas Solares</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="marcaPlaca">Marca da Placa</Label>
                  <Select
                    value={manualKitData.marcaPlaca}
                    onValueChange={(value) => setManualKitData(prev => ({ ...prev, marcaPlaca: value, modeloPlaca: '' }))}
                  >
                    <SelectTrigger id="marcaPlaca">
                      <SelectValue placeholder="Selecione a marca" />
                    </SelectTrigger>
                    <SelectContent>
                      {marcasPlacas.map((marca) => (
                        <SelectItem key={marca} value={marca}>
                          {marca}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="modeloPlaca">Modelo da Placa</Label>
                  <Select
                    value={manualKitData.modeloPlaca}
                    onValueChange={(value) => setManualKitData(prev => ({ ...prev, modeloPlaca: value }))}
                    disabled={!manualKitData.marcaPlaca}
                  >
                    <SelectTrigger id="modeloPlaca">
                      <SelectValue placeholder="Selecione o modelo" />
                    </SelectTrigger>
                    <SelectContent>
                      {modelosPlacas.map((modulo) => (
                        <SelectItem key={modulo.id} value={modulo.id}>
                          {modulo.modelo} ({modulo.potencia}W)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="quantidadePlacas">Quantidade de Placas</Label>
                  <Input
                    id="quantidadePlacas"
                    type="text"
                    inputMode="numeric"
                    value={editingQuantidadePlacas}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Permitir apenas números e campo vazio
                      if (value === '' || /^\d+$/.test(value)) {
                        setEditingQuantidadePlacas(value);
                        // Atualizar o valor numérico apenas se for válido
                        const numValue = value === '' ? 0 : parseInt(value, 10);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setManualKitData(prev => ({ ...prev, quantidadePlacas: numValue }));
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value === '') {
                        setEditingQuantidadePlacas('');
                        setManualKitData(prev => ({ ...prev, quantidadePlacas: 0 }));
                      } else {
                        const numValue = parseInt(value, 10);
                        if (!isNaN(numValue) && numValue > 0) {
                          setEditingQuantidadePlacas(numValue.toString());
                          setManualKitData(prev => ({ ...prev, quantidadePlacas: numValue }));
                        } else {
                          // Se o valor não for válido, limpar o campo
                          setEditingQuantidadePlacas('');
                          setManualKitData(prev => ({ ...prev, quantidadePlacas: 0 }));
                        }
                      }
                    }}
                    placeholder="Ex.: 20"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Informações dos Inversores</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="marcaInversor">Marca do Inversor</Label>
                  <Select
                    value={manualKitData.marcaInversor}
                    onValueChange={(value) => setManualKitData(prev => ({ ...prev, marcaInversor: value, modeloInversor: '' }))}
                  >
                    <SelectTrigger id="marcaInversor">
                      <SelectValue placeholder="Selecione a marca" />
                    </SelectTrigger>
                    <SelectContent>
                      {marcasInversores.map((marca) => (
                        <SelectItem key={marca} value={marca}>
                          {marca}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="modeloInversor">Modelo do Inversor</Label>
                  <Select
                    value={manualKitData.modeloInversor}
                    onValueChange={(value) => setManualKitData(prev => ({ ...prev, modeloInversor: value }))}
                    disabled={!manualKitData.marcaInversor}
                  >
                    <SelectTrigger id="modeloInversor">
                      <SelectValue placeholder="Selecione o modelo" />
                    </SelectTrigger>
                    <SelectContent>
                      {modelosInversores.map((inversor) => (
                        <SelectItem key={inversor.id} value={inversor.id}>
                          {inversor.modelo} ({inversor.potenciaNominal}W)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="quantidadeInversores">Quantidade de Inversores</Label>
                  <Input
                    id="quantidadeInversores"
                    type="text"
                    inputMode="numeric"
                    value={editingQuantidadeInversores}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Permitir apenas números e campo vazio
                      if (value === '' || /^\d+$/.test(value)) {
                        setEditingQuantidadeInversores(value);
                        // Atualizar o valor numérico apenas se for válido
                        const numValue = value === '' ? 0 : parseInt(value, 10);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setManualKitData(prev => ({ ...prev, quantidadeInversores: numValue }));
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value === '') {
                        setEditingQuantidadeInversores('');
                        setManualKitData(prev => ({ ...prev, quantidadeInversores: 0 }));
                      } else {
                        const numValue = parseInt(value, 10);
                        if (!isNaN(numValue) && numValue > 0) {
                          setEditingQuantidadeInversores(numValue.toString());
                          setManualKitData(prev => ({ ...prev, quantidadeInversores: numValue }));
                        } else {
                          // Se o valor não for válido, limpar o campo
                          setEditingQuantidadeInversores('');
                          setManualKitData(prev => ({ ...prev, quantidadeInversores: 0 }));
                        }
                      }
                    }}
                    placeholder="Ex.: 1"
                  />
                </div>
              </div>
            </div>

            {isLeadSolarHibrido && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Informações das Baterias</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="marcaBateria">Marca da Bateria</Label>
                    <Select
                      value={manualKitData.marcaBateria}
                      onValueChange={(value) =>
                        setManualKitData((prev) => ({ ...prev, marcaBateria: value, modeloBateria: "" }))
                      }
                    >
                      <SelectTrigger id="marcaBateria">
                        <SelectValue placeholder="Selecione a marca" />
                      </SelectTrigger>
                      <SelectContent>
                        {marcasBaterias.map((marca) => (
                          <SelectItem key={marca} value={marca}>
                            {marca}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="modeloBateria">Modelo da Bateria</Label>
                    <Select
                      value={manualKitData.modeloBateria}
                      onValueChange={(value) =>
                        setManualKitData((prev) => ({ ...prev, modeloBateria: value }))
                      }
                      disabled={!manualKitData.marcaBateria}
                    >
                      <SelectTrigger id="modeloBateria">
                        <SelectValue placeholder="Selecione o modelo" />
                      </SelectTrigger>
                      <SelectContent>
                        {modelosBaterias.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.modelo} ({b.energiaKwh} kWh)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="quantidadeBaterias">Quantidade de Baterias</Label>
                    <Input
                      id="quantidadeBaterias"
                      type="text"
                      inputMode="numeric"
                      value={editingQuantidadeBaterias}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d+$/.test(value)) {
                          setEditingQuantidadeBaterias(value);
                          const numValue = value === "" ? 0 : parseInt(value, 10);
                          if (!isNaN(numValue) && numValue >= 0) {
                            setManualKitData((prev) => ({ ...prev, quantidadeBaterias: numValue }));
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value === "") {
                          setEditingQuantidadeBaterias("");
                          setManualKitData((prev) => ({ ...prev, quantidadeBaterias: 0 }));
                        } else {
                          const numValue = parseInt(value, 10);
                          if (!isNaN(numValue) && numValue > 0) {
                            setEditingQuantidadeBaterias(numValue.toString());
                            setManualKitData((prev) => ({ ...prev, quantidadeBaterias: numValue }));
                          } else {
                            setEditingQuantidadeBaterias("");
                            setManualKitData((prev) => ({ ...prev, quantidadeBaterias: 0 }));
                          }
                        }
                      }}
                      placeholder="Ex.: 2"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Estrutura e Preço</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tipoEstrutura">Tipo de Estrutura</Label>
                  <Select
                    value={manualKitData.tipoEstrutura}
                    onValueChange={(value) => setManualKitData(prev => ({ ...prev, tipoEstrutura: value }))}
                  >
                    <SelectTrigger id="tipoEstrutura">
                      <SelectValue placeholder="Selecione o tipo de estrutura" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sem Estrutura">Sem Estrutura</SelectItem>
                      <SelectItem value="Colonial">Colonial</SelectItem>
                      <SelectItem value="Fibrocimento">Fibrocimento</SelectItem>
                      <SelectItem value="Laje">Laje</SelectItem>
                      <SelectItem value="Telhado Ondulado">Telhado Ondulado</SelectItem>
                      <SelectItem value="Telhado Metálico">Telhado Metálico</SelectItem>
                      <SelectItem value="Solo">Solo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="precoTotal">Preço Total dos Produtos (R$)</Label>
                  <Input
                    id="precoTotal"
                    type="text"
                    value={manualKitData.precoTotal === 0 ? '' : formatBrazilianCurrencyValue(manualKitData.precoTotal)}
                    onChange={(e) => {
                      const formatted = formatCurrencyInput(e.target.value);
                      const value = parseBrazilianCurrency(formatted);
                      setManualKitData(prev => ({ ...prev, precoTotal: value }));
                    }}
                    onKeyDown={(e) => {
                      // Permite apenas números e teclas de controle
                      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                      if (allowedKeys.includes(e.key) || /^\d$/.test(e.key)) {
                        return;
                      }
                      e.preventDefault();
                    }}
                    placeholder="0,00"
                  />
                </div>
              </div>
            </div>

            {/* Resumo do Kit Manual */}
            {manualKitData.modeloPlaca &&
              manualKitData.modeloInversor &&
              (!isLeadSolarHibrido ||
                (manualKitData.modeloBateria && manualKitData.quantidadeBaterias > 0)) && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Resumo do Kit</h4>
                {(() => {
                  const kitInfo = calculateManualKitInfo();
                  if (!kitInfo) return null;
                  
                  const placaSelecionada = modulos.find(m => m.id === manualKitData.modeloPlaca);
                  const inversorSelecionado = inversoresKitManual.find(i => i.id === manualKitData.modeloInversor);
                  const bateriaSelecionada = isLeadSolarHibrido
                    ? baterias.find((b) => b.id === manualKitData.modeloBateria)
                    : undefined;
                  
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p><strong>Potência Total dos Painéis:</strong> {kitInfo.potenciaTotalPaineis.toFixed(2)} kWp</p>
                        <p><strong>Total de Painéis:</strong> {kitInfo.totalPaineis} unidades</p>
                        {placaSelecionada && (
                          <p><strong>Placa:</strong> {placaSelecionada.marca} {placaSelecionada.modelo} ({placaSelecionada.potencia}W)</p>
                        )}
                      </div>
                      <div>
                        <p><strong>Potência Total dos Inversores:</strong> {kitInfo.potenciaTotalInversores.toFixed(2)} kW</p>
                        {inversorSelecionado && (
                          <p><strong>Inversor:</strong> {inversorSelecionado.marca} {inversorSelecionado.modelo} ({inversorSelecionado.potenciaNominal}W)</p>
                        )}
                        {isLeadSolarHibrido && bateriaSelecionada && (
                          <p>
                            <strong>Bateria:</strong> {bateriaSelecionada.marca} {bateriaSelecionada.modelo} (
                            {bateriaSelecionada.energiaKwh} kWh, {manualKitData.quantidadeBaterias}x)
                          </p>
                        )}
                        <p><strong>Valor Total:</strong> R$ {formatBrazilianCurrencyValue(manualKitData.precoTotal)}</p>
                        <p><strong>R$/Wp:</strong> R$ {formatBrazilianCurrencyValue(kitInfo.rPorWp)}</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <Button
              onClick={addManualKitToSelection}
              disabled={
                !manualKitData.marcaPlaca ||
                !manualKitData.modeloPlaca ||
                !manualKitData.marcaInversor ||
                !manualKitData.modeloInversor ||
                !manualKitData.tipoEstrutura ||
                manualKitData.precoTotal <= 0 ||
                (isLeadSolarHibrido &&
                  (!manualKitData.marcaBateria ||
                    !manualKitData.modeloBateria ||
                    !manualKitData.quantidadeBaterias ||
                    manualKitData.quantidadeBaterias <= 0))
              }
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Kit Manual
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Seleção de Kits Aldo */}
      {kitType === 'aldo' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Escolha do Kit - Aldo Solar</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setKitType(null)}
                >
                  Voltar
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Filtros para Consulta de Kits</h3>
            
            {/* Power filter */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div>
                <Label htmlFor="potenciaMin">Potência Mínima (kWp)</Label>
                <Input
                  id="potenciaMin"
                  type="number"
                  step="0.1"
                  value={potenciaMin}
                  onChange={(e) => setPotenciaMin(e.target.value)}
                  placeholder="Ex: 3.0"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Baseado no dimensionamento: {dimensionamentoData?.systemPowerKWp || 'N/A'} kWp
                </p>
              </div>
            </div>
            
            {/* Other filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtersData.Dados.filter(f => f.Id !== 3 && f.Id !== 13 && f.Id !== 208 && f.Id !== 1 && f.Id !== 6 && f.Id !== 8).map((filter) => (
                <div key={filter.Id}>
                  <Label htmlFor={`filter-${filter.Id}`}>{filter.Descricao}</Label>
                  {filter.Valores ? (
                    <Select
                      value={selectedFilters[filter.Id] || 'all'}
                      onValueChange={(value) => handleFilterChange(filter.Id, value)}
                    >
                      <SelectTrigger id={`filter-${filter.Id}`}>
                        <SelectValue placeholder={`Selecione ${filter.Descricao}`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {filter.Valores.map((valor) => (
                          <SelectItem key={valor.Id} value={valor.Id.toString()}>
                            {valor.Descricao}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : null}
                </div>
              ))}
            </div>
            
            <div className="flex gap-4">
              <Button onClick={generateTokenAndFetchKits} disabled={loading}>
                {loading ? 'Buscando Kits...' : 'Buscar Kits'}
              </Button>
              <Button variant="outline" onClick={regenerateToken} disabled={loading}>
                Renovar Token
              </Button>
            </div>
          </div>
          
          {products.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Resultados ({products.length} itens)</h3>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID Produto</TableHead>
                      <TableHead>Descrição do Kit</TableHead>
                      <TableHead>Preço</TableHead>
                      <TableHead>Potência (kWp)</TableHead>
                      <TableHead>R$/Wp</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.Codigo}>
                        <TableCell>{product.Codigo}</TableCell>
                        <TableCell>{product.Nome}</TableCell>
                        <TableCell>R$ {formatBrazilianCurrencyValue(product.Preco || 0)}</TableCell>
                        <TableCell>{formatBrazilianCurrencyValue(product.Potencia || 0)}</TableCell>
                        <TableCell>R$ {formatBrazilianCurrencyValue((product.Preco || 0) / ((product.Potencia || 1) * 1000))}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => fetchProductInputs(product)}
                            >
                              Ver Insumos
                            </Button>
                            <Button 
                              variant="default" 
                              size="sm" 
                              onClick={() => {
                                setKitToAdd(product);
                                setShowKitSelection(true);
                              }}
                              disabled={selectedKits.length >= 4 && !selectedKits.find(k => k.codigo === product.Codigo)}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Adicionar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </>
      )}
      
      {/* Dialog para seleção de quantidade */}
      <Dialog open={showKitSelection} onOpenChange={setShowKitSelection}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Kit à Seleção</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {kitToAdd && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p><strong>Kit:</strong> {kitToAdd.Nome}</p>
                <p><strong>Potência:</strong> {kitToAdd.Potencia?.toFixed(2)} kWp</p>
                <p><strong>Preço:</strong> R$ {formatBrazilianCurrencyValue(kitToAdd.Preco || 0)}</p>
              </div>
            )}
            
            <div>
              <Label htmlFor="kitQuantity">Quantidade</Label>
              <Input
                id="kitQuantity"
                type="number"
                min="1"
                value={kitQuantity}
                onChange={(e) => setKitQuantity(parseInt(e.target.value) || 1)}
                placeholder="1"
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowKitSelection(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={() => kitToAdd && addKitToSelection(kitToAdd, kitQuantity)}
                disabled={!kitToAdd || kitQuantity < 1}
              >
                Adicionar Kit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog para mostrar insumos */}
      <Dialog open={showInputs} onOpenChange={(open) => {
        if (!open) {
          handleShowInputsClose();
        } else {
          setShowInputs(true);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              Insumos do Produto: {selectedProduct?.Codigo} - {selectedProduct?.Nome}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh]">
            {productInputs ? (
              <div className="space-y-4 p-4">
                {/* Check if we have an Insumos array */}
                {productInputs.Insumos && Array.isArray(productInputs.Insumos) ? (
                  <div className="space-y-3">
                    {productInputs.Insumos.map((input: InsumoData, index: number) => (
                      <div key={index} className="border rounded-lg p-4 bg-white">
                        <h4 className="font-semibold text-lg mb-2">
                          {(input as any).Descricao || (input as any).descricao || 'Produto sem descrição'}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="font-medium">Quantidade:</span> {(input as any).Quantidade || (input as any).quantidade || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Valor:</span> R$ {typeof (input as any).Valor === 'number' ? formatBrazilianCurrencyValue((input as any).Valor) : (typeof (input as any).valor === 'number' ? formatBrazilianCurrencyValue((input as any).valor) : 'N/A')}
                          </div>
                          <div>
                            <span className="font-medium">Tipo:</span> {(input as any).Tipo || (input as any).tipo || 'N/A'}
                          </div>
                          {/* Special fields for inversor */}
                          {((input as any).Tipo?.toLowerCase().includes('inversor') || (input as any).tipo?.toLowerCase().includes('inversor')) && (
                            <>
                              {(input as any).LinkFolheto && (
                                <div>
                                  <span className="font-medium">Link do Folheto:</span>{' '}
                                  <a href={(input as any).LinkFolheto} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Abrir folheto
                                  </a>
                                </div>
                              )}
                              {(input as any).Imagem && (
                                <div>
                                  <span className="font-medium">Link da Imagem:</span>{' '}
                                  <a href={(input as any).Imagem} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Ver imagem
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                          {/* Special field for painel */}
                          {(input.Tipo?.toLowerCase().includes('painel') || input.tipo?.toLowerCase().includes('painel')) && (
                            <div>
                              <span className="font-medium">Potência:</span> {input.Potencia ? `${input.Potencia} W` : (input.potencia ? `${input.potencia} W` : 'N/A')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : Array.isArray(productInputs.Data) ? (
                  <div className="space-y-3">
                    {productInputs.Data.map((input: InsumoData, index: number) => (
                      <div key={index} className="border rounded-lg p-4 bg-white">
                        <h4 className="font-semibold text-lg mb-2">
                          {(input as any).descricao || (input as any).Descricao || (input as any).descricao_produto || (input as any).Descricao_Produto || 'Produto sem descrição'}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="font-medium">Quantidade:</span> {(input as any).quantidade || (input as any).Quantidade || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Valor:</span> R$ {typeof (input as any).valor === 'number' ? formatBrazilianCurrencyValue((input as any).valor) : ((input as any).Valor ? formatBrazilianCurrencyValue((input as any).Valor) : 'N/A')}
                          </div>
                          <div>
                            <span className="font-medium">Tipo:</span> {(input as any).tipo || (input as any).Tipo || 'N/A'}
                          </div>
                          {/* Special fields for inversor */}
                          {(input as any).tipo && ((input as any).tipo.toLowerCase().includes('inversor') || (input as any).Tipo?.toLowerCase().includes('inversor')) && (
                            <>
                              {(input as any).link_folheto && (
                                <div>
                                  <span className="font-medium">Link do Folheto:</span>{' '}
                                  <a href={(input as any).link_folheto} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Abrir folheto
                                  </a>
                                </div>
                              )}
                              {(input as any).imagem && (
                                <div>
                                  <span className="font-medium">Link da Imagem:</span>{' '}
                                  <a href={(input as any).imagem} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Ver imagem
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                          {/* Special field for painel */}
                          {input.tipo && (input.tipo.toLowerCase().includes('painel') || input.Tipo?.toLowerCase().includes('painel')) && (
                            <div>
                              <span className="font-medium">Potência:</span> {input.potencia ? `${input.potencia} W` : (input.Potencia ? `${input.Potencia} W` : 'N/A')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : typeof productInputs.Data === 'object' && productInputs.Data !== null ? (
                  <div className="space-y-3">
                    {/* When productInputs.Data is an object, we display it as a single insumo */}
                    <div className="border rounded-lg p-4 bg-white">
                      <h4 className="font-semibold text-lg mb-2">
                        {(productInputs.Data as any).descricao || (productInputs.Data as any).Descricao || (productInputs.Data as any).descricao_produto || (productInputs.Data as any).Descricao_Produto || 'Produto sem descrição'}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="font-medium">Quantidade:</span> {(productInputs.Data as any).quantidade || (productInputs.Data as any).Quantidade || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Valor:</span> R$ {typeof (productInputs.Data as any).valor === 'number' ? formatBrazilianCurrencyValue((productInputs.Data as any).valor) : ((productInputs.Data as any).Valor ? formatBrazilianCurrencyValue((productInputs.Data as any).Valor) : 'N/A')}
                        </div>
                        <div>
                          <span className="font-medium">Tipo:</span> {(productInputs.Data as any).tipo || (productInputs.Data as any).Tipo || 'N/A'}
                        </div>
                        {/* Special fields for inversor */}
                        {(productInputs.Data as any).tipo && ((productInputs.Data as any).tipo.toLowerCase().includes('inversor') || (productInputs.Data as any).Tipo?.toLowerCase().includes('inversor')) && (
                          <>
                            {(productInputs.Data as any).link_folheto && (
                              <div>
                                <span className="font-medium">Link do Folheto:</span>{' '}
                                <a href={(productInputs.Data as any).link_folheto} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  Abrir folheto
                                </a>
                              </div>
                            )}
                            {(productInputs.Data as any).imagem && (
                              <div>
                                <span className="font-medium">Link da Imagem:</span>{' '}
                                <a href={(productInputs.Data as any).imagem} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  Ver imagem
                                </a>
                              </div>
                            )}
                          </>
                        )}
                        {/* Special field for painel */}
                        {(productInputs.Data as any).tipo && ((productInputs.Data as any).tipo.toLowerCase().includes('painel') || (productInputs.Data as any).Tipo?.toLowerCase().includes('painel')) && (
                          <div>
                            <span className="font-medium">Potência:</span> {(productInputs.Data as any).potencia ? `${(productInputs.Data as any).potencia} W` : ((productInputs.Data as any).Potencia ? `${(productInputs.Data as any).Potencia} W` : 'N/A')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 bg-white">
                    <h4 className="font-semibold text-lg mb-2">Informações do Produto</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="font-medium">Descrição:</span> {String(productInputs)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="p-4">Carregando insumos...</p>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Navegação */}
      <div className="flex justify-between pt-4">
        <Button onClick={onBack} variant="outline">
          Voltar
        </Button>
        {isParceiro ? (
          <Button
            onClick={enviarParaPrecificacao}
            disabled={
              readOnly ||
              !canProceedToNext() ||
              (statusRevisao === 'aguardando_revisao' && !hasUnsavedChanges) ||
              statusRevisao === 'revisada' ||
              isEnviandoRevisao
            }
            className={
              statusRevisao === 'aguardando_revisao'
                ? 'bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400'
                : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400'
            }
            title={
              !canProceedToNext()
                ? "Salve os kits antes de enviar para precificação"
                : statusRevisao === 'aguardando_revisao'
                  ? "Aguardando precificação pelo administrador"
                  : "Enviar dimensionamento para precificação pelo administrador"
            }
          >
            {isEnviandoRevisao
              ? 'Enviando...'
              : statusRevisao === 'aguardando_revisao'
                ? 'Aguardando Precificação'
                : statusRevisao === 'revisada'
                  ? 'Precificação Concluída'
                  : 'Enviar para Precificação'}
          </Button>
        ) : (
          <Button 
            onClick={() => onNext(dimensionamentoId)} 
            disabled={readOnly || !canProceedToNext()}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            title={
              selectedKits.length === 0 
                ? "Selecione pelo menos um kit antes de prosseguir"
                : !isDataSaved 
                  ? "Salve os kits antes de prosseguir"
                  : hasUnsavedChanges 
                    ? "Existem alterações não salvas. Salve os kits antes de prosseguir"
                    : "Clique para prosseguir para a próxima etapa"
            }
          >
            Próximo
          </Button>
        )}
      </div>
    </div>
  );
}
